import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format, addDays, addWeeks } from "date-fns";
import {
  ArrowLeft, ArrowRight, User, Ruler, FileText, DollarSign,
  Plus, Minus, Trash2, Search, X, Check, Banknote, Smartphone,
  CreditCard, Receipt, Loader2, Download, Printer, UserPlus,
  Phone, Mail, MapPin, ChevronRight, Edit2, Save, Package,
  Clock, History, AlertCircle, Shirt, Eye, ChevronDown, Scissors,
  Copy, MoreVertical, Wand2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";
import html2pdf from "html2pdf.js";
import { useAIAssistant } from "@/context/AIAssistantContext";

// Import MTM Step Components
import { MTMFabricStep, MTMMeasurementsStep, MTMStylesStep } from "@/components/pos";
import SuitMockupPreview from "@/components/SuitMockupPreview";
import { consumeStyleCart } from "@/data/styleCart";
import { fieldGarmentBucket, fieldStateKey } from "@/data/codeReverseLookup";
import OrderActions from "@/components/OrderActions";
import MakeToggle from "@/components/MakeToggle";
import GarmentJumpNav from "@/components/pos/GarmentJumpNav";
import StyleCartPanel from "@/components/pos/StyleCartPanel";
import InvoiceItemDetails from "@/components/pos/InvoiceItemDetails";
import { MAKE_PROFILES, calculateGarmentPrice as makeCalculateGarmentPrice } from "@/lib/makePricing";
import { useFabricCatalog } from "@/contexts/FabricCatalogContext";
import { useMobileMode } from "@/hooks/useMobileMode";
import MobileModeToggle from "@/components/mobile/MobileModeToggle";
import MobileStepper from "@/components/mobile/MobileStepper";
import MobileWizardFooter from "@/components/mobile/MobileWizardFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pricing Structure (matches /admin/price-list)
// RC Construction Labor
const RC_LABOR = {
  "Fused": { "2piece": 350, "3piece": 450, "jacket": 250, "trouser": 130, "pant": 130, "vest": 100, "shirt": 80, "overcoat": 500, "tuxedo": 500 },
  "Half Canvas": { "2piece": 450, "3piece": 575, "jacket": 320, "trouser": 130, "pant": 130, "vest": 125, "shirt": 80, "overcoat": 500, "tuxedo": 500 },
  "Full Canvas": { "2piece": 600, "3piece": 750, "jacket": 420, "trouser": 130, "pant": 130, "vest": 150, "shirt": 80, "overcoat": 500, "tuxedo": 500 },
};

// CTD Construction Labor (MX CMT)
const CTD_LABOR = {
  "CTD Fused": { "2piece": 115, "3piece": 155, "jacket": 75, "trouser": 40, "pant": 40, "vest": 40, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD Half Canvas": { "2piece": 145, "3piece": 185, "jacket": 105, "trouser": 40, "pant": 40, "vest": 40, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD High-End Half Canvas": { "2piece": 176, "3piece": 217, "jacket": 135, "trouser": 41, "pant": 41, "vest": 41, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD Full Canvas": { "2piece": 219, "3piece": 263, "jacket": 175, "trouser": 44, "pant": 44, "vest": 44, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD Machine Half Canvas": { "2piece": 176, "3piece": 217, "jacket": 135, "trouser": 41, "pant": 41, "vest": 41, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD 1/3 Handmade Half Canvas": { "2piece": 239, "3piece": 296, "jacket": 182, "trouser": 57, "pant": 57, "vest": 57, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD Machine Full Canvas": { "2piece": 219, "3piece": 263, "jacket": 175, "trouser": 44, "pant": 44, "vest": 44, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD 1/3 Handmade Full Canvas": { "2piece": 282, "3piece": 342, "jacket": 222, "trouser": 60, "pant": 60, "vest": 60, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD 1/2 Handmade Full Canvas": { "2piece": 390, "3piece": 520, "jacket": 260, "trouser": 130, "pant": 130, "vest": 130, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
  "CTD Full Handmade Full Canvas": { "2piece": 575, "3piece": 705, "jacket": 430, "trouser": 145, "pant": 145, "vest": 130, "shirt": 80, "overcoat": 300, "tuxedo": 300 },
};

const ALL_LABOR = { ...RC_LABOR, ...CTD_LABOR };

const PRICING_CONFIG = {
  fabricUsage: {
    "2piece": 3.5,
    "3piece": 4.2,
    "jacket": 2.2,
    "trouser": 1.3,
    "pant": 1.3,
    "vest": 0.7,
    "shirt": 2.0,
    "overcoat": 4.0,
    "tuxedo": 3.5,
  },
  markup: 3.0,
};

// Calculate garment price based on fabric selection and construction type.
// Delegates to the central make-aware helper so RC and CTD pipelines stay
// consistent across POS, Quick Order, and Price List.
const calculateGarmentPrice = (fabricPrice, garmentType, constructionType = "Half Canvas") => {
  return makeCalculateGarmentPrice(fabricPrice, garmentType, constructionType);
};

// Steps - Extended with sub-steps for MTM Order
const STEPS = [
  { id: 1, name: "Customer", icon: User },
  { id: 2, name: "Profile", icon: History },
  { id: "3a", name: "Fabric", icon: Package, parent: 3 },
  { id: "3b", name: "Measurements", icon: Ruler, parent: 3 },
  { id: "3c", name: "Styles", icon: Scissors, parent: 3 },
  { id: 4, name: "Summary", icon: FileText },
  { id: 5, name: "Payment", icon: DollarSign }
];

// Payment methods
const PAYMENT_METHODS = [
  { value: "cash", label: "Cash", icon: Banknote, color: "bg-green-500" },
  { value: "card", label: "Card", icon: CreditCard, color: "bg-blue-500" },
  { value: "venmo", label: "Venmo", icon: Smartphone, color: "bg-cyan-500" },
  { value: "zelle", label: "Zelle", icon: Smartphone, color: "bg-purple-500" },
  { value: "check", label: "Check", icon: FileText, color: "bg-slate-500" }
];

// Garment Types
const GARMENT_TYPES = [
  { value: "2piece", label: "2-Piece Suit", price: 995 },
  { value: "3piece", label: "3-Piece Suit", price: 1295 },
  { value: "jacket", label: "Sport Coat / Blazer", price: 695 },
  { value: "trouser", label: "Trousers", price: 295 },
  { value: "vest", label: "Vest", price: 295 },
  { value: "shirt", label: "Dress Shirt", price: 195 },
  { value: "overcoat", label: "Overcoat", price: 1495 },
  { value: "tuxedo", label: "Tuxedo", price: 1495 }
];

const DEFAULT_TAX_RATE = 8.375;

const AdminPOSPage = () => {
  const navigate = useNavigate();
  const invoiceRef = useRef(null);
  const { registerPageHandlers, updatePageState } = useAIAssistant();
  const { mobileMode } = useMobileMode();
  
  // Step state - now supports string step IDs for sub-steps
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Customer state
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [clients, setClients] = useState([]);
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClientForm, setNewClientForm] = useState({
    name: "", email: "", phone: "", address: ""
  });
  const [creatingClient, setCreatingClient] = useState(false);
  
  // Customer Profile state
  const [clientOrders, setClientOrders] = useState([]);
  const [clientMeasurements, setClientMeasurements] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  
  // MTM Order state - Garment Selection
  const [garmentType, setGarmentType] = useState("");
  const [constructionType, setConstructionType] = useState("Half Canvas");
  // Make ("rc" or "ctd") drives which construction pipeline + markup applies.
  const [make, setMake] = useState("rc");
  const [includeJacket, setIncludeJacket] = useState(false);
  const [includeTrouser, setIncludeTrouser] = useState(false);
  const [includeVest, setIncludeVest] = useState(false);
  const [includeShirt, setIncludeShirt] = useState(false);
  // Reset all garment-include toggles at once (used by reset / new-order flows below)
  
  // Size Charts
  const [sizeCharts, setSizeCharts] = useState({ jacket: [], pant: [], vest: [], shirt: [] });
  
  // ===== FABRIC & LINING STATE (Step 3a) =====
  // The single-selection fields stay as the canonical "primary" used by the
  // downstream price/PDF/save flows. The per-garment maps below are the
  // Quick-Order-style UI sources of truth for Step 3a — a sync effect
  // derives the primary from whichever garment is filled first.
  const [fabricSelection, setFabricSelection] = useState(null);
  const [liningSelection, setLiningSelection] = useState(null);
  const [liningType, setLiningType] = useState("Full");
  const [pipingSelection, setPipingSelection] = useState(null);

  // Per-garment selections (new — drives the GarmentFabricSection UI). Keys:
  // jacket / pant / vest / shirt / overcoat → rc_code string.
  const [fabricByGarment, setFabricByGarment] = useState({});
  const [liningByGarment, setLiningByGarment] = useState({});
  const [buttonByGarment, setButtonByGarment] = useState({});

  // Catalog lookup used to enrich a picked rc_code back into the rich
  // {rc_code, price, composition, color, pattern, ...} object that the
  // downstream price/PDF/save flows already expect.
  const { catalog: fabricCatalog } = useFabricCatalog();
  const _fabricByCode = useMemo(() => {
    const idx = new Map();
    const push = (arr, src) => {
      for (const f of arr || []) {
        if (f?.rc_code) idx.set(f.rc_code, { ...f, source: src });
      }
    };
    push(fabricCatalog?.rc?.suits, "RC");
    push(fabricCatalog?.rc?.overcoats, "RC");
    push(fabricCatalog?.rc?.shirts, "RC");
    push(fabricCatalog?.mx, "MX");
    return idx;
  }, [fabricCatalog]);
  const _liningByCode = useMemo(() => {
    const idx = new Map();
    for (const l of fabricCatalog?.rc?.linings || []) {
      if (l?.rc_code) idx.set(l.rc_code, { ...l, source: "RC" });
    }
    return idx;
  }, [fabricCatalog]);
  const _buttonByCode = useMemo(() => {
    const idx = new Map();
    const bk = fabricCatalog?.rc?.buttons || {};
    for (const cat of ["suit", "overcoat", "shirt"]) {
      for (const b of bk[cat] || []) {
        if (b?.code) idx.set(b.code, b);
      }
    }
    return idx;
  }, [fabricCatalog]);

  // Derive the canonical single selection from the per-garment map.
  // Order of preference: jacket → overcoat → pant → vest → shirt.
  const _pickPrimary = (byGarment, lookup) => {
    if (!byGarment || typeof byGarment !== "object") return null;
    for (const key of ["jacket", "overcoat", "pant", "vest", "shirt"]) {
      const code = byGarment[key];
      if (!code) continue;
      const obj = lookup.get(code);
      if (obj) return obj;
      // Fallback: pass the bare code through so downstream can still display.
      return { rc_code: code };
    }
    return null;
  };

  // Sync the single fabric/lining/button selection from per-garment.
  // We intentionally ONLY push to the single state — the reverse direction
  // (single → per-garment) is handled inline by the legacy fabric picker
  // when the new section isn't being used.
  useEffect(() => {
    const primary = _pickPrimary(fabricByGarment, _fabricByCode);
    if (primary) setFabricSelection(primary);
  }, [fabricByGarment, _fabricByCode]);
  useEffect(() => {
    const primary = _pickPrimary(liningByGarment, _liningByCode);
    if (primary) setLiningSelection(primary);
  }, [liningByGarment, _liningByCode]);
  useEffect(() => {
    if (!buttonByGarment) return;
    const jCode = buttonByGarment.jacket || buttonByGarment.overcoat;
    if (jCode) {
      const obj = _buttonByCode.get(jCode);
      if (obj) setJacketButtonSelection(obj);
    }
    const vCode = buttonByGarment.vest;
    if (vCode) {
      const obj = _buttonByCode.get(vCode);
      if (obj) setVestButtonSelection(obj);
    }
  }, [buttonByGarment, _buttonByCode]);

  // ===== BUTTONS STATE (Step 3a) =====
  const [jacketButtonSelection, setJacketButtonSelection] = useState(null);
  const [vestButtonSelection, setVestButtonSelection] = useState(null);
  const [vestMatchesJacket, setVestMatchesJacket] = useState(true);
  
  // ===== JACKET STATE (Step 3b & 3c) =====
  const [jacketSize, setJacketSize] = useState("");
  const [jacketStartingSpecs, setJacketStartingSpecs] = useState({});
  const [jacketAdjustments, setJacketAdjustments] = useState({
    chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, 
    right_sleeve_length: 0, left_sleeve_length: 0, 
    back_length: 0, front_length: 0, 
    right_cuff: 0, left_cuff: 0,
    button_stance: 0
  });
  const [jacketPosture, setJacketPosture] = useState({
    stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0
  });
  const [jacketRemarks, setJacketRemarks] = useState("");
  const [jacketStyling, setJacketStyling] = useState({
    canvas_construction: "Half Canvas", front_closure: "2-Button", lapel_type: "Notch",
    lapel_width: "3\" (7.6cm)", gorge_line: "Standard", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
    ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
    exterior_stitch: "None", sleeve_buttonholes: "4 Kissing", vent: "Double Vent",
    lapel_buttonhole: "None", under_collar_monogram: "",
    lining_color: "", sleeve_lining_color: ""
  });
  const [jacketStylingRemarks, setJacketStylingRemarks] = useState("");
  
  // ===== TROUSER STATE (Step 3b & 3c) =====
  const [trouserSize, setTrouserSize] = useState("");
  const [trouserStartingSpecs, setTrouserStartingSpecs] = useState({});
  const [trouserAdjustments, setTrouserAdjustments] = useState({
    waist: 0, seat: 0, urise: 0, thigh: 0, 
    right_pant_length: 0, left_pant_length: 0, 
    knee: 0, bottom: 0
  });
  const [trouserPosture, setTrouserPosture] = useState({
    front_rise: 0, back_rise: 0
  });
  const [trouserRemarks, setTrouserRemarks] = useState("");
  const [pantStyling, setPantStyling] = useState({
    body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
    back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
    back_pocket_style: "Double Besom", front_closure: "Button & Tab",
    waistband_style: "Standard", waistband_width: "1.5\" (3.8cm)",
    pleat_depth: "—", watch_pocket: "None", inner_watch_pocket: "None",
    front_pocket_bartack: "None", front_pocket_stitch: "None",
    pant_lining_color: ""
  });
  const [pantStylingRemarks, setPantStylingRemarks] = useState("");
  
  // ===== VEST STATE (Step 3b & 3c) =====
  const [vestSize, setVestSize] = useState("");
  const [vestStartingSpecs, setVestStartingSpecs] = useState({});
  const [vestAdjustments, setVestAdjustments] = useState({
    chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0
  });
  const [vestRemarks, setVestRemarks] = useState("");
  const [vestStyling, setVestStyling] = useState({
    lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
    pocket_number: "4 Pockets", back: "Lining"
  });
  const [vestStylingRemarks, setVestStylingRemarks] = useState("");
  const [shirtStyling, setShirtStyling] = useState({
    collar_style:  "Spread",
    collar_height: "Standard 4cm",
    collar_stays:  "Removable",
    cuff_style:    "Single Button Barrel",
    placket:       "Standard",
    pocket:        "Standard",
    back:          "Plain",
    bottom:        "Curved",
    button_style:  "Standard",
    monogram:      "None",
  });
  const [shirtStylingRemarks, setShirtStylingRemarks] = useState("");
  
  // ===== MOCKUP PREVIEW STATE =====
  const [showMockupPreview, setShowMockupPreview] = useState(false);
  
  // ===== ORDER NOTES & OPTIONS =====
  const [orderNotes, setOrderNotes] = useState("");
  const [rushOrder, setRushOrder] = useState(false);
  
  // Pricing
  const [customPrice, setCustomPrice] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("fixed");
  const [taxRate, setTaxRate] = useState(DEFAULT_TAX_RATE);
  
  // Payment state
  const [payments, setPayments] = useState([]);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState("cash");
  const [currentPaymentAmount, setCurrentPaymentAmount] = useState("");
  const [isProcessingStripe, setIsProcessingStripe] = useState(false);
  
  // Invoice state
  const [showInvoice, setShowInvoice] = useState(false);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  // ===== MULTI-ITEM ORDER STATE =====
  const [savedItems, setSavedItems] = useState([]);

  // Save current item and start new one
  const saveCurrentItemAndAddNew = () => {
    if (!garmentType) {
      toast.error("Please select a garment type first");
      return;
    }
    
    // Calculate price for this item
    const itemPrice = fabricSelection?.price 
      ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
      : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0;
    
    // Create item object with all measurements including starting specs
    const newItem = {
      id: Date.now(),
      garmentType,
      garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
      constructionType,
      price: itemPrice,
      fabricSelection: fabricSelection ? { ...fabricSelection } : null,
      liningSelection: liningSelection ? { ...liningSelection } : null,
      liningType,
      // Jacket data
      jacketSize,
      jacketStartingSpecs: { ...jacketStartingSpecs },
      jacketAdjustments: { ...jacketAdjustments },
      jacketPosture: { ...jacketPosture },
      jacketStyling: { ...jacketStyling },
      jacketRemarks,
      jacketStylingRemarks,
      // Trouser data
      trouserSize,
      trouserStartingSpecs: { ...trouserStartingSpecs },
      trouserAdjustments: { ...trouserAdjustments },
      trouserPosture: { ...trouserPosture },
      pantStyling: { ...pantStyling },
      trouserRemarks,
      pantStylingRemarks,
      // Vest data
      vestSize,
      vestStartingSpecs: { ...vestStartingSpecs },
      vestAdjustments: { ...vestAdjustments },
      vestStyling: { ...vestStyling },
      vestRemarks,
      vestStylingRemarks,
      // Flags
      includeJacket,
      includeTrouser,
      includeVest
    };
    
    // Add to saved items
    setSavedItems(prev => [...prev, newItem]);
    
    // Reset for new item (keep customer selected)
    resetItemForNew();
    
    toast.success(`${newItem.garmentLabel} added to order`);
    
    // Go back to Profile step to select new garment
    setCurrentStep(2);
  };

  // Reset item fields for new item (keep customer)
  const resetItemForNew = () => {
    setGarmentType("");
    setIncludeJacket(false);
    setIncludeTrouser(false);
    setIncludeVest(false);
    setIncludeShirt(false);
    setFabricSelection(null);
    setLiningSelection(null);
    setLiningType("Full");
    setPipingSelection(null);
    setJacketButtonSelection(null);
    setVestButtonSelection(null);
    setVestMatchesJacket(true);
    // Reset jacket
    setJacketSize("");
    setJacketStartingSpecs({});
    setJacketAdjustments({ 
      chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, 
      right_sleeve_length: 0, left_sleeve_length: 0, 
      back_length: 0, front_length: 0, 
      right_cuff: 0, left_cuff: 0,
      button_stance: 0
    });
    setJacketPosture({ stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0 });
    setJacketRemarks("");
    setJacketStyling({
      canvas_construction: "Half Canvas", front_closure: "2-Button", lapel_type: "Notch",
      lapel_width: "3\" (7.6cm)", gorge_line: "Standard", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
      ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
      exterior_stitch: "None", sleeve_buttonholes: "4 Kissing", vent: "Double Vent",
      lapel_buttonhole: "None", under_collar_monogram: ""
    });
    setJacketStylingRemarks("");
    // Reset trouser
    setTrouserSize("");
    setTrouserStartingSpecs({});
    setTrouserAdjustments({ 
      waist: 0, seat: 0, urise: 0, thigh: 0, 
      right_pant_length: 0, left_pant_length: 0, 
      knee: 0, bottom: 0 
    });
    setTrouserPosture({ front_rise: 0, back_rise: 0 });
    setTrouserRemarks("");
    setPantStyling({
      body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
      back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
      back_pocket_style: "Double Welt", front_closure: "Button & Tab",
      waistband_style: "Standard", waistband_width: "1.5\" (3.8cm)"
    });
    setPantStylingRemarks("");
    // Reset vest
    setVestSize("");
    setVestStartingSpecs({});
    setVestAdjustments({ chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 });
    setVestRemarks("");
    setVestStyling({
      lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
      pocket_number: "4 Pockets", back: "Lining"
    });
    setVestStylingRemarks("");
  };

  // Remove saved item
  const removeSavedItem = (itemId) => {
    setSavedItems(prev => prev.filter(item => item.id !== itemId));
    toast.info("Item removed from order");
  };

  // Edit saved item - load it back into current form
  const editSavedItem = (itemId, targetStep = 2) => {
    const item = savedItems.find(i => i.id === itemId);
    if (!item) return;
    
    // Remove from saved items
    setSavedItems(prev => prev.filter(i => i.id !== itemId));
    
    // Load item data into current form
    setGarmentType(item.garmentType);
    setFabricSelection(item.fabricSelection);
    setLiningSelection(item.liningSelection);
    setLiningType(item.liningType || "Full");
    
    // Set garment flags
    const gType = item.garmentType;
    setIncludeJacket(["2piece", "3piece", "jacket", "tuxedo", "overcoat"].includes(gType));
    setIncludeTrouser(["2piece", "3piece", "trouser", "pant", "tuxedo"].includes(gType));
    setIncludeVest(["3piece", "vest"].includes(gType));
    setIncludeShirt(["shirt", "dress_shirt"].includes(gType));
    
    // Load measurements and styling
    if (item.jacketSize) setJacketSize(item.jacketSize);
    if (item.jacketStartingSpecs) setJacketStartingSpecs(item.jacketStartingSpecs);
    if (item.jacketAdjustments) setJacketAdjustments(item.jacketAdjustments);
    if (item.jacketPosture) setJacketPosture(item.jacketPosture);
    if (item.jacketStyling) setJacketStyling(item.jacketStyling);
    if (item.jacketRemarks) setJacketRemarks(item.jacketRemarks);
    if (item.jacketStylingRemarks) setJacketStylingRemarks(item.jacketStylingRemarks);
    if (item.trouserSize) setTrouserSize(item.trouserSize);
    if (item.trouserStartingSpecs) setTrouserStartingSpecs(item.trouserStartingSpecs);
    if (item.trouserAdjustments) setTrouserAdjustments(item.trouserAdjustments);
    if (item.trouserPosture) setTrouserPosture(item.trouserPosture);
    if (item.pantStyling) setPantStyling(item.pantStyling);
    if (item.trouserRemarks) setTrouserRemarks(item.trouserRemarks);
    if (item.pantStylingRemarks) setPantStylingRemarks(item.pantStylingRemarks);
    if (item.vestSize) setVestSize(item.vestSize);
    if (item.vestStartingSpecs) setVestStartingSpecs(item.vestStartingSpecs);
    if (item.vestAdjustments) setVestAdjustments(item.vestAdjustments);
    if (item.vestStyling) setVestStyling(item.vestStyling);
    if (item.vestRemarks) setVestRemarks(item.vestRemarks);
    if (item.vestStylingRemarks) setVestStylingRemarks(item.vestStylingRemarks);
    
    toast.info(`Editing ${item.garmentLabel}`);
    setCurrentStep(targetStep); // Go to specified step
  };

  // Edit saved item - go to Fabric step
  const editSavedItemFabric = (itemId) => {
    editSavedItem(itemId, "3a");
  };

  // Edit saved item - go to Measurements step
  const editSavedItemMeasurements = (itemId) => {
    editSavedItem(itemId, "3b");
  };

  // Edit saved item - go to Styles step
  const editSavedItemStyles = (itemId) => {
    editSavedItem(itemId, "3c");
  };

  // Edit current item - navigate to specific step
  const editCurrentItemSection = (step) => {
    setCurrentStep(step);
    toast.info(`Editing ${step === "3a" ? "Fabric" : step === "3b" ? "Measurements" : step === "3c" ? "Styles" : "item"}`);
  };

  // Copy whole order (duplicate item)
  const copyWholeOrder = (itemId) => {
    const item = savedItems.find(i => i.id === itemId);
    if (!item) return;
    
    const newItem = {
      ...item,
      id: Date.now(),
    };
    setSavedItems(prev => [...prev, newItem]);
    toast.success(`${item.garmentLabel} duplicated`);
  };

  // Copy only style from item - creates new item with style only (measurements reset)
  const copyOrderStyle = (itemId) => {
    const item = savedItems.find(i => i.id === itemId);
    if (!item) return;
    
    // Default measurements
    const defaultJacketAdjustments = { chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, right_sleeve_length: 0, left_sleeve_length: 0, back_length: 0, front_length: 0, right_cuff: 0, left_cuff: 0, button_stance: 0 };
    const defaultJacketPosture = { stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0 };
    const defaultTrouserAdjustments = { waist: 0, seat: 0, urise: 0, thigh: 0, right_pant_length: 0, left_pant_length: 0, knee: 0, bottom: 0 };
    const defaultTrouserPosture = { front_rise: 0, back_rise: 0 };
    const defaultVestAdjustments = { chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 };
    
    // Create new item with style only (measurements reset to defaults)
    const newItem = {
      id: Date.now(),
      garmentType: item.garmentType,
      garmentLabel: item.garmentLabel,
      price: item.price,
      fabricSelection: item.fabricSelection ? { ...item.fabricSelection } : null,
      liningSelection: item.liningSelection ? { ...item.liningSelection } : null,
      liningType: item.liningType,
      // Keep styling
      jacketStyling: item.jacketStyling ? { ...item.jacketStyling } : null,
      pantStyling: item.pantStyling ? { ...item.pantStyling } : null,
      vestStyling: item.vestStyling ? { ...item.vestStyling } : null,
      // Reset measurements to defaults
      jacketSize: "",
      jacketAdjustments: { ...defaultJacketAdjustments },
      jacketPosture: { ...defaultJacketPosture },
      trouserSize: "",
      trouserAdjustments: { ...defaultTrouserAdjustments },
      trouserPosture: { ...defaultTrouserPosture },
      vestSize: "",
      vestAdjustments: { ...defaultVestAdjustments },
    };
    
    setSavedItems(prev => [...prev, newItem]);
    toast.success(`${item.garmentLabel} duplicated with style only`);
  };

  // Copy only measurements from item - creates new item with measurements only (style reset)
  const copyOrderMeasurements = (itemId) => {
    const item = savedItems.find(i => i.id === itemId);
    if (!item) return;
    
    // Default styling
    const defaultJacketStyling = {
      canvas_construction: "Half Canvas", front_closure: "2-Button", lapel_type: "Notch",
      lapel_width: "3\" (7.6cm)", gorge_line: "Standard", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
      ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
      exterior_stitch: "None", sleeve_buttonholes: "4 Kissing", vent: "Double Vent",
      lapel_buttonhole: "None", under_collar_monogram: "",
      lining_color: "", sleeve_lining_color: ""
    };
    const defaultPantStyling = {
      body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
      back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
      back_pocket_style: "Double Besom", front_closure: "Button & Tab",
      waistband_style: "Standard", waistband_width: "1.5\" (3.8cm)",
      pleat_depth: "—", watch_pocket: "None", inner_watch_pocket: "None",
      front_pocket_bartack: "None", front_pocket_stitch: "None",
      pant_lining_color: ""
    };
    const defaultVestStyling = {
      lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
      pocket_number: "4 Pockets", back: "Lining"
    };
    
    // Create new item with measurements only (style reset to defaults)
    const newItem = {
      id: Date.now(),
      garmentType: item.garmentType,
      garmentLabel: item.garmentLabel,
      price: item.price,
      fabricSelection: item.fabricSelection ? { ...item.fabricSelection } : null,
      liningSelection: item.liningSelection ? { ...item.liningSelection } : null,
      liningType: item.liningType,
      // Reset styling to defaults
      jacketStyling: { ...defaultJacketStyling },
      pantStyling: { ...defaultPantStyling },
      vestStyling: { ...defaultVestStyling },
      // Keep measurements
      jacketSize: item.jacketSize || "",
      jacketAdjustments: item.jacketAdjustments ? { ...item.jacketAdjustments } : {},
      jacketPosture: item.jacketPosture ? { ...item.jacketPosture } : {},
      trouserSize: item.trouserSize || "",
      trouserAdjustments: item.trouserAdjustments ? { ...item.trouserAdjustments } : {},
      trouserPosture: item.trouserPosture ? { ...item.trouserPosture } : {},
      vestSize: item.vestSize || "",
      vestAdjustments: item.vestAdjustments ? { ...item.vestAdjustments } : {},
    };
    
    setSavedItems(prev => [...prev, newItem]);
    toast.success(`${item.garmentLabel} duplicated with measurements only`);
  };

  // ===== AI ASSISTANT INTEGRATION =====
  // Register page handlers for AI Assistant to control this page
  useEffect(() => {
    const handlers = {
      // Navigation
      goToStep: (params) => {
        const stepMap = {
          "customer": 1,
          "profile": 2,
          "fabric": "3a",
          "measurements": "3b",
          "styles": "3c",
          "summary": 4,
          "payment": 5
        };
        const step = stepMap[params?.step?.toLowerCase()] || params?.step;
        if (step) {
          setCurrentStep(step);
          toast.info(`Navigated to ${params?.step}`);
          return true;
        }
        return false;
      },
      
      // Customer selection
      selectCustomer: (params) => {
        const client = clients.find(c => 
          c.name?.toLowerCase().includes(params?.name?.toLowerCase()) ||
          c.email?.toLowerCase() === params?.email?.toLowerCase() ||
          c.id === params?.id
        );
        if (client) {
          setSelectedClient(client);
          fetchClientProfile(client);
          toast.success(`Selected customer: ${client.name}`);
          return true;
        }
        toast.error(`Customer "${params?.name || params?.email}" not found`);
        return false;
      },
      
      // Garment type
      setGarmentType: (params) => {
        const type = params?.value || params?.garmentType;
        const garmentMap = {
          "2-piece suit": "2piece",
          "2 piece suit": "2piece",
          "two piece suit": "2piece",
          "3-piece suit": "3piece",
          "3 piece suit": "3piece",
          "three piece suit": "3piece",
          "sport coat": "jacket",
          "blazer": "jacket",
          "jacket": "jacket",
          "trousers": "trouser",
          "pants": "trouser",
          "trouser": "trouser",
          "vest": "vest",
          "waistcoat": "vest",
          "dress shirt": "shirt",
          "shirt": "shirt",
          "overcoat": "overcoat",
          "tuxedo": "tuxedo",
          "tux": "tuxedo"
        };
        const normalizedType = garmentMap[type?.toLowerCase()] || type;
        handleGarmentTypeChange(normalizedType);
        toast.success(`Set garment type to ${GARMENT_TYPES.find(g => g.value === normalizedType)?.label || type}`);
        return true;
      },
      
      // Sizes
      setJacketSize: (params) => {
        setJacketSize(params?.size || params?.value);
        toast.success(`Set jacket size to ${params?.size || params?.value}`);
        return true;
      },
      setTrouserSize: (params) => {
        setTrouserSize(params?.size || params?.value);
        toast.success(`Set trouser size to ${params?.size || params?.value}`);
        return true;
      },
      setVestSize: (params) => {
        setVestSize(params?.size || params?.value);
        toast.success(`Set vest size to ${params?.size || params?.value}`);
        return true;
      },
      
      // Adjustments
      setJacketAdjustment: (params) => {
        const { field, value } = params;
        setJacketAdjustments(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
        return true;
      },
      setTrouserAdjustment: (params) => {
        const { field, value } = params;
        setTrouserAdjustments(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
        return true;
      },
      setVestAdjustment: (params) => {
        const { field, value } = params;
        setVestAdjustments(prev => ({ ...prev, [field]: parseFloat(value) || 0 }));
        return true;
      },
      
      // Styling
      setJacketStyling: (params) => {
        setJacketStyling(prev => ({ ...prev, ...params }));
        toast.success("Updated jacket styling");
        return true;
      },
      setPantStyling: (params) => {
        setPantStyling(prev => ({ ...prev, ...params }));
        toast.success("Updated pant styling");
        return true;
      },
      setVestStyling: (params) => {
        setVestStyling(prev => ({ ...prev, ...params }));
        toast.success("Updated vest styling");
        return true;
      },
      
      // Order options
      setRushOrder: (params) => {
        const value = params?.value === true || params?.value === "true" || params?.enabled;
        setRushOrder(value);
        toast.success(value ? "Rush order enabled" : "Rush order disabled");
        return true;
      },
      setDiscount: (params) => {
        const value = parseFloat(params?.value) || 0;
        const type = params?.type || "fixed";
        setDiscount(value);
        setDiscountType(type);
        toast.success(`Applied ${type === "percent" ? value + "%" : "$" + value} discount`);
        return true;
      },
      setOrderNotes: (params) => {
        setOrderNotes(params?.notes || params?.value || "");
        return true;
      },
      
      // Payment
      addPayment: (params) => {
        const amount = parseFloat(params?.amount);
        const method = params?.method || "cash";
        if (amount > 0) {
          setPayments(prev => [...prev, {
            id: `pay-${Date.now()}`,
            method,
            amount,
            date: new Date().toISOString()
          }]);
          toast.success(`Added ${method} payment of $${amount}`);
          return true;
        }
        return false;
      },
      payInFull: (params) => {
        const method = params?.method || "cash";
        payFull(method);
        return true;
      },
      
      // Multi-item actions
      saveItem: () => {
        saveCurrentItemAndAddNew();
        return true;
      },
      
      // Complete sale
      completeSale: async () => {
        await completeSale();
        return true;
      }
    };
    
    const unregister = registerPageHandlers("POS - MTM Order", handlers);
    return unregister;
  }, [clients, registerPageHandlers]);
  
  // Update page state for AI to read
  useEffect(() => {
    updatePageState("POS - MTM Order", {
      currentStep,
      selectedClient: selectedClient ? {
        id: selectedClient.id,
        name: selectedClient.name,
        email: selectedClient.email
      } : null,
      garmentType,
      garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
      includeJacket,
      includeTrouser,
      includeVest,
      fabricSelection: fabricSelection ? {
        code: fabricSelection.rc_code,
        price: fabricSelection.price
      } : null,
      jacketSize,
      trouserSize,
      vestSize,
      jacketStyling,
      pantStyling,
      vestStyling,
      rushOrder,
      discount,
      discountType,
      savedItemsCount: savedItems.length,
      savedItems: savedItems.map(item => ({
        garmentType: item.garmentType,
        garmentLabel: item.garmentLabel,
        price: item.price
      })),
      availableClients: clients.slice(0, 10).map(c => ({ name: c.name, email: c.email }))
    });
  }, [currentStep, selectedClient, garmentType, fabricSelection, jacketSize, trouserSize, vestSize, 
      jacketStyling, pantStyling, vestStyling, rushOrder, discount, savedItems, clients, updatePageState]);

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  // Consume any styles staged by the RC Code Book and apply them to the right styling state.
  useEffect(() => {
    const { items } = consumeStyleCart();
    if (!items.length) return;
    const byBucket = { jacket: {}, pant: {}, vest: {}, shirt: {} };
    for (const it of items) {
      const bucket = fieldGarmentBucket(it.field);
      const key = fieldStateKey(it.field);
      if (bucket && key) byBucket[bucket][key] = it.value;
    }
    if (Object.keys(byBucket.jacket).length) setJacketStyling((s) => ({ ...s, ...byBucket.jacket }));
    if (Object.keys(byBucket.pant).length)   setPantStyling((s)   => ({ ...s, ...byBucket.pant   }));
    if (Object.keys(byBucket.vest).length)   setVestStyling((s)   => ({ ...s, ...byBucket.vest   }));
    if (Object.keys(byBucket.shirt).length)  setShirtStyling((s)  => ({ ...s, ...byBucket.shirt  }));
    toast.success(`Applied ${items.length} style${items.length === 1 ? "" : "s"} from RC Code Book`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cascade the globally-selected Body Lining (Step 3a Fabric, Lining &
  // Buttons) into the per-garment lining slots used by the Styles step AND
  // into the sibling Piping selection on Step 3a itself.
  //   • jacket: lining_color + sleeve_lining_color + inner_facing_piping
  //   • pant:   pant_lining_color
  //   • vest:   vest_lining_color
  //   • piping selection (Step 3a)
  //
  // Sticky overrides: once the user manually picks a different Piping (or
  // sleeve / inner-facing-piping) after a cascade, the override sticks even
  // when Body Lining changes again. We track each follower's last
  // auto-applied value in a ref; if the current value still matches that
  // ref, we consider it "in sync" and cascade. If it differs, the user has
  // overridden it and we leave it alone.
  const liningCascadeRef = useRef({
    piping: null,
    sleeve: null,
    facing: null,
    pant: null,
    vest: null,
  });
  useEffect(() => {
    const code = liningSelection?.rc_code;
    if (!code) return;
    const tracker = liningCascadeRef.current;

    // Piping selection (separate page-level state)
    if (!pipingSelection || pipingSelection.rc_code === tracker.piping) {
      setPipingSelection(liningSelection);
      tracker.piping = code;
    }

    // Jacket lining_color always follows Body Lining (this row IS Body Lining
    // mirrored into the Styles state).
    setJacketStyling((s) => {
      const next = { ...s, lining_color: code };
      if (!s.sleeve_lining_color || s.sleeve_lining_color === tracker.sleeve) {
        next.sleeve_lining_color = code;
        tracker.sleeve = code;
      }
      if (!s.inner_facing_piping || s.inner_facing_piping === tracker.facing) {
        next.inner_facing_piping = code;
        tracker.facing = code;
      }
      return next;
    });

    setPantStyling((s) => {
      if (!s.pant_lining_color || s.pant_lining_color === tracker.pant) {
        tracker.pant = code;
        return { ...s, pant_lining_color: code };
      }
      return s;
    });
    setVestStyling((s) => {
      if (!s.vest_lining_color || s.vest_lining_color === tracker.vest) {
        tracker.vest = code;
        return { ...s, vest_lining_color: code };
      }
      return s;
    });
    // We intentionally read pipingSelection inside but don't add it as a dep
    // so a manual piping change doesn't re-fire the whole cascade.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liningSelection?.rc_code]);

  // ===========================================================================
  // BUTTON CASCADE (Step 3a Jacket/Vest/Pant Button → Step 3c styling fields)
  // Same sticky-override pattern as the lining cascade — track the last value
  // we auto-pushed per follower, and stop overwriting once the user manually
  // edits it in the Styles step.
  // ===========================================================================
  const buttonCascadeRef = useRef({
    jacket_button_style: null,
    jacket_front_button_code: null,
    pant_front_button_code: null,
    vest_front_button_code: null,
  });
  useEffect(() => {
    const code = jacketButtonSelection?.code || jacketButtonSelection?.rc_code;
    if (!code) return;
    const t = buttonCascadeRef.current;
    setJacketStyling((s) => {
      const upd = { ...s };
      if (!s.button_style || s.button_style === t.jacket_button_style) {
        upd.button_style = code;
        t.jacket_button_style = code;
      }
      if (!s.front_button_code || s.front_button_code === t.jacket_front_button_code) {
        upd.front_button_code = code;
        t.jacket_front_button_code = code;
      }
      return upd;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jacketButtonSelection?.code, jacketButtonSelection?.rc_code]);
  useEffect(() => {
    const code = vestButtonSelection?.code || vestButtonSelection?.rc_code;
    if (!code) return;
    const t = buttonCascadeRef.current;
    setVestStyling((s) => {
      if (!s.front_button_code || s.front_button_code === t.vest_front_button_code) {
        t.vest_front_button_code = code;
        return { ...s, front_button_code: code };
      }
      return s;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vestButtonSelection?.code, vestButtonSelection?.rc_code]);
  useEffect(() => {
    const code = buttonByGarment?.pant;
    if (!code) return;
    const t = buttonCascadeRef.current;
    setPantStyling((s) => {
      if (!s.pant_front_button_code || s.pant_front_button_code === t.pant_front_button_code) {
        t.pant_front_button_code = code;
        return { ...s, pant_front_button_code: code };
      }
      return s;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buttonByGarment?.pant]);

  // ===========================================================================
  // REVERSE SYNC (single state → per-garment maps)
  // When a saved order is restored (or "Last Order" template seeds the legacy
  // single selections), pre-populate the per-garment maps so the new
  // GarmentFabricSection pickers show what was loaded. Only fires when the
  // matching per-garment slot is empty so we don't overwrite a user's pick.
  // ===========================================================================
  useEffect(() => {
    const code = fabricSelection?.rc_code;
    if (!code) return;
    setFabricByGarment((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      for (const g of ["jacket", "pant", "vest"]) {
        if (!next[g]) {
          next[g] = code;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [fabricSelection?.rc_code]);
  useEffect(() => {
    const code = liningSelection?.rc_code;
    if (!code) return;
    setLiningByGarment((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      for (const g of ["jacket", "pant", "vest"]) {
        if (!next[g]) {
          next[g] = code;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [liningSelection?.rc_code]);
  useEffect(() => {
    // Hydrate the per-garment button map from whatever the loaded order /
    // last-order seed has populated in the styling and button-selection
    // state. We touch ONLY empty per-garment slots so the user's manual
    // edits during the wizard are preserved.
    const jCode = jacketButtonSelection?.code || jacketButtonSelection?.rc_code;
    const vCode = vestButtonSelection?.code || vestButtonSelection?.rc_code;
    const pCode = pantStyling?.pant_front_button_code;
    const sCode = shirtStyling?.button_style;
    const oCode = jacketStyling?.front_button_code; // overcoat shares jacket styling for button
    if (!jCode && !vCode && !pCode && !sCode && !oCode) return;
    setButtonByGarment((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      if (jCode && !next.jacket)   { next.jacket   = jCode; changed = true; }
      if (vCode && !next.vest)     { next.vest     = vCode; changed = true; }
      if (pCode && !next.pant)     { next.pant     = pCode; changed = true; }
      if (sCode && !next.shirt)    { next.shirt    = sCode; changed = true; }
      if (oCode && !next.overcoat) { next.overcoat = oCode; changed = true; }
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    jacketButtonSelection?.code, jacketButtonSelection?.rc_code,
    vestButtonSelection?.code,   vestButtonSelection?.rc_code,
    pantStyling?.pant_front_button_code,
    shirtStyling?.button_style,
    jacketStyling?.front_button_code,
  ]);

  // Check for Quick Order data
  useEffect(() => {
    const quickOrderData = sessionStorage.getItem("quickOrderData");
    if (quickOrderData) {
      try {
        const data = JSON.parse(quickOrderData);
        if (data.from_quick_order) {
          // Clear the data from session storage
          sessionStorage.removeItem("quickOrderData");
          
          // Load selected client if available
          if (data.selected_client) {
            setSelectedClient(data.selected_client);
          }
          
          // Set garment type
          const garmentTypeMap = {
            "2-Piece Suit": "2piece",
            "3-Piece Suit": "3piece",
            "Sport Coat": "jacket",
            "Trousers": "trouser",
            "Vest": "vest",
            "Dress Shirt": "shirt",
            "Overcoat": "overcoat",
            "Tuxedo": "tuxedo"
          };
          const mappedType = garmentTypeMap[data.garment_type] || "2piece";
          setGarmentType(mappedType);
          
          // Set includes based on garment type
          setIncludeJacket(["2piece", "3piece", "jacket", "tuxedo", "overcoat"].includes(mappedType));
          setIncludeTrouser(["2piece", "3piece", "trouser", "tuxedo"].includes(mappedType));
          setIncludeVest(["3piece", "vest"].includes(mappedType));
          setIncludeShirt(["shirt", "dress_shirt"].includes(mappedType));
          
          // Load jacket measurements
          if (data.jacket_measurements) {
            const adjMap = {};
            Object.entries(data.jacket_measurements).forEach(([key, val]) => {
              if (val && typeof val === 'object' && 'adjustment' in val) {
                adjMap[key] = val.adjustment;
              }
            });
            if (Object.keys(adjMap).length > 0) {
              setJacketAdjustments(prev => ({ ...prev, ...adjMap }));
            }
          }
          
          // Load pant measurements
          if (data.pant_measurements) {
            const adjMap = {};
            Object.entries(data.pant_measurements).forEach(([key, val]) => {
              if (val && typeof val === 'object' && 'adjustment' in val) {
                adjMap[key] = val.adjustment;
              }
            });
            if (Object.keys(adjMap).length > 0) {
              setTrouserAdjustments(prev => ({ ...prev, ...adjMap }));
            }
          }
          
          // Load vest measurements
          if (data.vest_measurements) {
            const adjMap = {};
            Object.entries(data.vest_measurements).forEach(([key, val]) => {
              if (val && typeof val === 'object' && 'adjustment' in val) {
                adjMap[key] = val.adjustment;
              }
            });
            if (Object.keys(adjMap).length > 0) {
              setVestAdjustments(prev => ({ ...prev, ...adjMap }));
            }
          }
          
          // Load styling
          if (data.jacket_styling) {
            setJacketStyling(prev => ({ ...prev, ...data.jacket_styling }));
          }
          if (data.pant_styling) {
            setPantStyling(prev => ({ ...prev, ...data.pant_styling }));
          }
          if (data.vest_styling) {
            setVestStyling(prev => ({ ...prev, ...data.vest_styling }));
          }
          
          // Set notes
          if (data.notes) {
            setJacketRemarks(data.notes);
          }
          
          const successMsg = data.selected_client 
            ? `Quick Order loaded with customer: ${data.selected_client.name}` 
            : "Quick Order data loaded! Select a customer to continue.";
          toast.success(successMsg);
        }
      } catch (e) {
        console.error("Error loading Quick Order data:", e);
      }
    }
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, sizeRes] = await Promise.all([
        axios.get(`${API}/crm/clients`),
        axios.get(`${API}/size-chart/all`)
      ]);
      setClients(clientsRes.data);
      setSizeCharts(sizeRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Fetch client profile
  const fetchClientProfile = async (client) => {
    setLoadingProfile(true);
    try {
      const [ordersRes, measurementsRes] = await Promise.all([
        axios.get(`${API}/mtm-orders?client_id=${client.id}`).catch(() => ({ data: [] })),
        axios.get(`${API}/measurements/${client.email}`).catch(() => ({ data: null }))
      ]);
      setClientOrders(ordersRes.data || []);
      setClientMeasurements(measurementsRes.data);
    } catch (error) {
      console.error("Error fetching profile:", error);
    } finally {
      setLoadingProfile(false);
    }
  };

  // Price calculation
  const orderPrice = useMemo(() => {
    if (customPrice) return parseFloat(customPrice) || 0;
    // If fabric is selected, calculate price based on fabric
    if (fabricSelection?.price) {
      return calculateGarmentPrice(fabricSelection.price, garmentType, constructionType);
    }
    // Fallback to default garment price
    const garment = GARMENT_TYPES.find(g => g.value === garmentType);
    return garment?.price || 0;
  }, [garmentType, customPrice, fabricSelection, constructionType]);

  const totals = useMemo(() => {
    // Calculate total from saved items + current item
    const savedItemsTotal = savedItems.reduce((sum, item) => sum + item.price, 0);
    const currentItemPrice = orderPrice;
    const subtotal = savedItemsTotal + currentItemPrice;
    
    const rushFee = rushOrder ? 100 : 0;
    const subtotalWithRush = subtotal + rushFee;
    const discountAmount = discountType === "percent" ? (subtotalWithRush * discount / 100) : discount;
    const taxableAmount = Math.max(0, subtotalWithRush - discountAmount);
    const taxAmount = taxableAmount * (taxRate / 100);
    const total = taxableAmount + taxAmount;
    const totalPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const balanceDue = Math.max(0, total - totalPaid);
    
    return { subtotal, savedItemsTotal, currentItemPrice, rushFee, discountAmount, taxableAmount, taxAmount, total, totalPaid, balanceDue };
  }, [orderPrice, savedItems, rushOrder, discount, discountType, taxRate, payments]);

  // Filtered clients
  const filteredClients = useMemo(() => {
    if (!clientSearch) return clients.slice(0, 10);
    const search = clientSearch.toLowerCase();
    return clients.filter(c => 
      c.name?.toLowerCase().includes(search) || 
      c.email?.toLowerCase().includes(search) ||
      c.phone?.includes(search)
    ).slice(0, 10);
  }, [clients, clientSearch]);

  // Navigation - Updated for sub-steps
  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedClient !== null;
      case 2: return garmentType !== "";
      case "3a": return true; // Fabric is optional but recommended
      case "3b": return true; // Measurements are optional
      case "3c": return true; // Styles are optional
      case 4: return true;
      case 5: return totals.balanceDue === 0;
      default: return false;
    }
  };

  // Get the order of steps for navigation
  const stepOrder = [1, 2, "3a", "3b", "3c", 4, 5];
  
  const nextStep = () => {
    if (!canProceed()) return;
    
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex < stepOrder.length - 1) {
      const nextStepId = stepOrder[currentIndex + 1];
      
      // Fetch client profile when moving from step 1 to 2
      if (currentStep === 1 && selectedClient) {
        fetchClientProfile(selectedClient);
      }
      
      setCurrentStep(nextStepId);
    }
  };

  const prevStep = () => {
    const currentIndex = stepOrder.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(stepOrder[currentIndex - 1]);
    }
  };
  
  // Get step display name for progress bar
  const getStepDisplayIndex = (stepId) => {
    if (typeof stepId === 'number') {
      if (stepId <= 2) return stepId;
      return stepId + 2; // Account for 3 sub-steps compressed into one visual step
    }
    // Sub-steps 3a, 3b, 3c all show as step 3
    return 3;
  };

  // Client functions
  const selectClient = (client) => {
    setSelectedClient(client);
    setClientSearch("");
    toast.success(`Selected ${client.name}`);
  };

  const createNewClient = async () => {
    if (!newClientForm.name.trim()) {
      toast.error("Customer name is required");
      return;
    }
    setCreatingClient(true);
    try {
      const res = await axios.post(`${API}/crm/clients`, {
        name: newClientForm.name,
        email: newClientForm.email || "",
        phone: newClientForm.phone || "",
        address: newClientForm.address || "",
        status: "active"
      });
      setSelectedClient(res.data);
      setClients([...clients, res.data]);
      setShowNewClientForm(false);
      setNewClientForm({ name: "", email: "", phone: "", address: "" });
      toast.success(`Created: ${res.data.name}`);
    } catch (error) {
      toast.error("Failed to create customer");
    } finally {
      setCreatingClient(false);
    }
  };

  // Garment type change handler
  const handleGarmentTypeChange = (value) => {
    setGarmentType(value);
    // Auto-set garment checkboxes
    setIncludeJacket(value.includes("piece") || value === "jacket" || value === "tuxedo" || value === "overcoat");
    setIncludeTrouser(value.includes("piece") || value === "trouser" || value === "tuxedo");
    setIncludeVest(value === "3piece");
  };

  // Payment functions
  const addPayment = () => {
    const amount = parseFloat(currentPaymentAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setPayments([...payments, {
      id: `pay-${Date.now()}`,
      method: currentPaymentMethod,
      amount,
      date: new Date().toISOString()
    }]);
    setCurrentPaymentAmount("");
  };

  const payFull = (method) => {
    if (totals.balanceDue <= 0) return;
    setPayments([...payments, {
      id: `pay-${Date.now()}`,
      method,
      amount: totals.balanceDue,
      date: new Date().toISOString()
    }]);
  };

  const removePayment = (id) => setPayments(payments.filter(p => p.id !== id));

  // Stripe card payment
  const initiateStripePayment = async () => {
    if (!selectedClient) {
      toast.error("Please select a customer first");
      return;
    }
    if (totals.balanceDue <= 0) {
      toast.error("No balance due");
      return;
    }

    setIsProcessingStripe(true);
    try {
      // Build items description
      const allItems = [...savedItems];
      if (garmentType) {
        allItems.push({ garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label || garmentType });
      }
      const itemsDescription = allItems.map(i => i.garmentLabel || i.garmentType).join(", ");

      const response = await axios.post(`${API}/payments/create-checkout`, {
        amount: parseFloat(totals.balanceDue.toFixed(2)),
        customer_name: selectedClient.name,
        customer_email: selectedClient.email || null,
        items_description: itemsDescription || "MTM Order",
        origin_url: window.location.origin,
        metadata: {
          client_id: selectedClient.id,
          garment_type: garmentType
        }
      });

      if (response.data.success && response.data.checkout_url) {
        // Store pending order info in sessionStorage for when we return
        sessionStorage.setItem('pendingStripePayment', JSON.stringify({
          session_id: response.data.session_id,
          amount: totals.balanceDue,
          transaction_id: response.data.transaction_id
        }));
        
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("Failed to create checkout session");
      }
    } catch (error) {
      console.error("Stripe payment error:", error);
      toast.error(error.response?.data?.detail || "Failed to initiate card payment");
    } finally {
      setIsProcessingStripe(false);
    }
  };

  // Check for Stripe payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const sessionId = urlParams.get('session_id');

    if (paymentStatus === 'success' && sessionId) {
      // Poll for payment status
      const checkPaymentStatus = async () => {
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          if (response.data.payment_status === 'paid') {
            const pendingPayment = JSON.parse(sessionStorage.getItem('pendingStripePayment') || '{}');
            const amount = pendingPayment.amount || response.data.amount_total;
            
            // Add the payment to the payments list
            setPayments(prev => [...prev, {
              id: `stripe-${sessionId}`,
              method: 'card',
              amount: amount,
              date: new Date().toISOString(),
              stripe_session_id: sessionId
            }]);
            
            toast.success(`Card payment of $${amount.toFixed(2)} successful!`);
            sessionStorage.removeItem('pendingStripePayment');
            
            // Clear URL params
            window.history.replaceState({}, document.title, window.location.pathname);
          } else if (response.data.status === 'expired') {
            toast.error("Payment session expired. Please try again.");
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (error) {
          console.error("Error checking payment status:", error);
        }
      };
      
      checkPaymentStatus();
    } else if (paymentStatus === 'cancelled') {
      toast.info("Payment was cancelled");
      sessionStorage.removeItem('pendingStripePayment');
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Complete sale
  const completeSale = async () => {
    if (totals.balanceDue > 0) {
      toast.error("Please complete payment first");
      return;
    }
    setIsLoading(true);
    try {
      // Build all items to save (saved items + current item if garment selected)
      const allItems = [...savedItems];
      
      // Add current item if a garment type is selected
      if (garmentType) {
        const currentItemPrice = fabricSelection?.price 
          ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
          : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0;
          
        allItems.push({
          id: Date.now(),
          garmentType,
          garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
          price: currentItemPrice,
          fabricSelection: fabricSelection ? { ...fabricSelection } : null,
          liningSelection: liningSelection ? { ...liningSelection } : null,
          liningType,
          pipingSelection: pipingSelection ? { ...pipingSelection } : null,
          jacketButtonSelection,
          vestButtonSelection,
          vestMatchesJacket,
          jacketSize,
          jacketStartingSpecs: { ...jacketStartingSpecs },
          jacketAdjustments: { ...jacketAdjustments },
          jacketPosture: { ...jacketPosture },
          jacketRemarks,
          jacketStyling: { ...jacketStyling },
          jacketStylingRemarks,
          trouserSize,
          trouserStartingSpecs: { ...trouserStartingSpecs },
          trouserAdjustments: { ...trouserAdjustments },
          trouserPosture: { ...trouserPosture },
          trouserRemarks,
          pantStyling: { ...pantStyling },
          pantStylingRemarks,
          vestSize,
          vestStartingSpecs: { ...vestStartingSpecs },
          vestAdjustments: { ...vestAdjustments },
          vestRemarks,
          vestStyling: { ...vestStyling },
          vestStylingRemarks,
          includeJacket,
          includeTrouser,
          includeVest
        });
      }
      
      if (allItems.length === 0) {
        toast.error("Please add at least one item to the order");
        setIsLoading(false);
        return;
      }

      // Get base order number and invoice number
      const [mtmNumRes, invoiceNumRes] = await Promise.all([
        axios.get(`${API}/mtm-orders/next-number`),
        axios.get(`${API}/invoices/next-number`)
      ]);

      const baseOrderNumber = mtmNumRes.data.next_number;
      const invoiceNumber = invoiceNumRes.data.next_number;
      
      const createdOrderNumbers = [];
      const createdOrderIds = [];
      
      // Create MTM Order for each item
      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];
        const orderNumber = allItems.length > 1 
          ? `${baseOrderNumber}-${i + 1}` 
          : baseOrderNumber;
        
        // Determine garment flags from the item
        const itemIncludeJacket = item.includeJacket ?? ["2piece", "3piece", "jacket", "tuxedo", "overcoat"].includes(item.garmentType);
        const itemIncludeTrouser = item.includeTrouser ?? ["2piece", "3piece", "trouser", "pant", "tuxedo"].includes(item.garmentType);
        const itemIncludeVest = item.includeVest ?? ["3piece", "vest"].includes(item.garmentType);
        
        const mtmOrderData = {
          order_number: orderNumber,
          client_id: selectedClient.id,
          client_name: selectedClient.name,
          client_email: selectedClient.email || "",
          client_phone: selectedClient.phone || "",
          garment_type: item.garmentLabel || item.garmentType,
          status: "pending",
          
          // Fabric & Lining
          construction_type: item.constructionType || constructionType,
          fabric_selection: item.fabricSelection,
          fabric_code: item.fabricSelection?.rc_code || "",
          lining_selection: item.liningSelection,
          lining_code: item.liningSelection?.rc_code || "",
          lining_type: item.liningType || "Full",
          piping_selection: item.pipingSelection || null,
          piping_code: item.pipingSelection?.rc_code || "",
          
          // Buttons
          jacket_button_selection: item.jacketButtonSelection || null,
          vest_button_selection: item.vestButtonSelection || null,
          vest_matches_jacket: item.vestMatchesJacket ?? true,
          
          // Sizes
          jacket_size: item.jacketSize || "",
          pant_size: item.trouserSize || "",
          vest_size: item.vestSize || "",
          
          // Jacket measurements & styling
          jacket_measurements: itemIncludeJacket ? {
            tryon_size: item.jacketSize || "",
            starting_specs: item.jacketStartingSpecs || {},
            adjustments: item.jacketAdjustments || {},
            posture: item.jacketPosture || {},
            remarks: item.jacketRemarks || "",
            styling: item.jacketStyling || {},
            styling_remarks: item.jacketStylingRemarks || ""
          } : null,
          
          // Pant measurements & styling
          pant_measurements: itemIncludeTrouser ? {
            tryon_size: item.trouserSize || "",
            starting_specs: item.trouserStartingSpecs || {},
            adjustments: item.trouserAdjustments || {},
            posture: item.trouserPosture || {},
            remarks: item.trouserRemarks || "",
            styling: item.pantStyling || {},
            styling_remarks: item.pantStylingRemarks || ""
          } : null,
          
          // Vest measurements & styling
          vest_measurements: itemIncludeVest ? {
            tryon_size: item.vestSize || "",
            starting_specs: item.vestStartingSpecs || {},
            adjustments: item.vestAdjustments || {},
            remarks: item.vestRemarks || "",
            styling: item.vestStyling || {},
            styling_remarks: item.vestStylingRemarks || ""
          } : null,
          
          // Legacy fields for backwards compatibility
          jacket_adjustments: itemIncludeJacket ? (item.jacketAdjustments || null) : null,
          pant_adjustments: itemIncludeTrouser ? (item.trouserAdjustments || null) : null,
          vest_adjustments: itemIncludeVest ? (item.vestAdjustments || null) : null,
          jacket_styling: itemIncludeJacket ? (item.jacketStyling || null) : null,
          pant_styling: itemIncludeTrouser ? (item.pantStyling || null) : null,
          vest_styling: itemIncludeVest ? (item.vestStyling || null) : null,
          
          // Notes
          notes: orderNotes,
          rush_order: rushOrder,
          
          // Pricing (individual item price)
          price: item.price,
          discount: 0, // Discount applied at order level in invoice
          tax_rate: 0,
          tax_amount: 0,
          total: item.price,
          amount_paid: 0,
          balance_due: 0,
          
          // Dates
          order_date: new Date().toISOString().split('T')[0],
          date_wanted: format(addWeeks(new Date(), rushOrder ? 2 : 4), "yyyy-MM-dd"),
          invoice_number: invoiceNumber,
          from_pos: true
        };

        const mtmRes = await axios.post(`${API}/mtm-orders`, mtmOrderData);
        if (mtmRes?.data?.id) createdOrderIds.push(mtmRes.data.id);
        createdOrderNumbers.push(orderNumber);
      }

      // Create Invoice with all items
      const invoiceItems = allItems.map((item, idx) => ({
        description: `Custom ${item.garmentLabel || item.garmentType} (${constructionType})${item.fabricSelection ? ` - Fabric: ${item.fabricSelection.rc_code}` : ""}`,
        quantity: 1,
        unit_price: item.price,
        total: item.price
      }));
      
      if (rushOrder) {
        invoiceItems.push({ description: "Rush Order Fee", quantity: 1, unit_price: 100, total: 100 });
      }
      
      const invoiceData = {
        invoice_number: invoiceNumber,
        pos_order_number: createdOrderNumbers.join(", "),
        client_name: selectedClient.name,
        client_email: selectedClient.email || "",
        client_phone: selectedClient.phone || "",
        client_address: selectedClient.address || "",
        items: invoiceItems,
        subtotal: totals.subtotal + totals.rushFee,
        tax_rate: taxRate,
        tax_amount: totals.taxAmount,
        discount: totals.discountAmount,
        total: totals.total,
        deposit_paid: totals.totalPaid,
        balance_due: 0,
        status: "paid",
        due_date: format(addDays(new Date(), 30), "yyyy-MM-dd"),
        estimated_arrival: format(addWeeks(new Date(), rushOrder ? 2 : 4), "yyyy-MM-dd"),
        notes: orderNotes
      };

      const invoiceRes = await axios.post(`${API}/invoices`, invoiceData);
      
      setCurrentInvoice({
        ...invoiceRes.data,
        mtm_order_number: createdOrderNumbers.join(", "),
        mtm_order_ids: createdOrderIds,
        first_mtm_order_id: createdOrderIds[0] || "",
        garment_type: allItems.map(i => i.garmentLabel || i.garmentType).join(", "),
        fabric_code: allItems.map(i => i.fabricSelection?.rc_code).filter(Boolean).join(", "),
        lining_code: allItems.map(i => i.liningSelection?.rc_code).filter(Boolean).join(", "),
        payments
      });
      setShowInvoice(true);
      toast.success(`${allItems.length} MTM Order${allItems.length > 1 ? 's' : ''} created!`);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to complete sale");
    } finally {
      setIsLoading(false);
    }
  };

  // Print/PDF
  const handlePrint = () => {
    if (!invoiceRef.current) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html><html><head><title>Invoice ${currentInvoice?.invoice_number}</title>
      <style>* { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: Arial, sans-serif; padding: 20px; background: white; color: #333; }
      @media print { body { padding: 0; } @page { margin: 0.5in; } }</style>
      </head><body>${invoiceRef.current.innerHTML}</body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); printWindow.close(); }, 250);
  };

  const handleExportPdf = async () => {
    if (!invoiceRef.current) return;
    setIsExportingPdf(true);
    try {
      await html2pdf().set({
        margin: [0.3, 0.3, 0.3, 0.3],
        filename: `Invoice-${currentInvoice?.invoice_number}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      }).from(invoiceRef.current).save();
      toast.success("PDF exported");
    } catch { toast.error("Failed to export PDF"); }
    finally { setIsExportingPdf(false); }
  };

  const resetOrder = () => {
    setSelectedClient(null);
    setGarmentType("");
    setIncludeJacket(false);
    setIncludeTrouser(false);
    setIncludeVest(false);
    setIncludeShirt(false);
    // Reset fabric & lining
    setFabricSelection(null);
    setLiningSelection(null);
    setLiningType("Full");
    setPipingSelection(null);
    // Reset buttons
    setJacketButtonSelection(null);
    setVestButtonSelection(null);
    setVestMatchesJacket(true);
    // Reset jacket
    setJacketSize("");
    setJacketStartingSpecs({});
    setJacketAdjustments({ 
      chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, 
      right_sleeve_length: 0, left_sleeve_length: 0, 
      back_length: 0, front_length: 0, 
      right_cuff: 0, left_cuff: 0 
    });
    setJacketPosture({ stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0 });
    setJacketRemarks("");
    setJacketStyling({
      canvas_construction: "Half Canvas", front_closure: "2-Button", lapel_type: "Notch",
      lapel_width: "3\" (7.6cm)", gorge_line: "Standard", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
      ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
      exterior_stitch: "None", sleeve_buttonholes: "4 Kissing", vent: "Double Vent",
      lapel_buttonhole: "None", under_collar_monogram: ""
    });
    setJacketStylingRemarks("");
    // Reset trouser
    setTrouserSize("");
    setTrouserStartingSpecs({});
    setTrouserAdjustments({ 
      waist: 0, seat: 0, urise: 0, thigh: 0, 
      right_pant_length: 0, left_pant_length: 0, 
      knee: 0, bottom: 0 
    });
    setTrouserPosture({ front_rise: 0, back_rise: 0 });
    setTrouserRemarks("");
    setPantStyling({
      body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
      back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
      back_pocket_style: "Double Welt", front_closure: "Button & Tab",
      waistband_style: "Standard", waistband_width: "1.5\" (3.8cm)"
    });
    setPantStylingRemarks("");
    // Reset vest
    setVestSize("");
    setVestStartingSpecs({});
    setVestAdjustments({ chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 });
    setVestRemarks("");
    setVestStyling({
      lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
      pocket_number: "4 Pockets", back: "Lining"
    });
    setVestStylingRemarks("");
    // Reset order options
    setOrderNotes("");
    setRushOrder(false);
    setCustomPrice("");
    setDiscount(0);
    setPayments([]);
    setSavedItems([]); // Clear saved items
    setShowInvoice(false);
    setCurrentInvoice(null);
    setCurrentStep(1);
  };

  return (
    <div className="admin-screen min-h-screen bg-[#0a1628] text-white">
      {/* Header */}
      <header className="bg-[#111c2e] border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate("/admin")} className="text-slate-400 hover:text-white">
                <ArrowLeft size={24} />
              </button>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Ruler size={24} className="text-amber-400" />
                MTM Order
              </h1>
            </div>
            <MobileModeToggle />
          </div>
        </div>
      </header>

      {/* Compact mobile stepper — only visible when body.mobile-mode is on */}
      <MobileStepper
        steps={STEPS}
        currentStep={currentStep}
        stepOrder={stepOrder}
        onStepChange={setCurrentStep}
        testId="pos-mobile-stepper"
      />

      {/* Progress Steps (desktop) — Updated for sub-steps */}
      <div className="pos-step-nav-desktop bg-[#111c2e] border-b border-white/10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between overflow-x-auto">
            {STEPS.map((step, idx) => {
              // Determine if this step is active, completed, or pending
              const stepIndex = stepOrder.indexOf(step.id);
              const currentIndex = stepOrder.indexOf(currentStep);
              const isActive = currentStep === step.id;
              const isCompleted = stepIndex < currentIndex;
              const isSubStep = typeof step.id === 'string';
              
              // For visual grouping, check if this is a sub-step
              const showSeparator = idx < STEPS.length - 1 && 
                !(isSubStep && STEPS[idx + 1]?.parent === step.parent);
              
              return (
                <div key={step.id} className="flex items-center">
                  <button
                    onClick={() => stepIndex <= currentIndex && setCurrentStep(step.id)}
                    disabled={stepIndex > currentIndex}
                    className={`flex items-center gap-2 px-2 sm:px-3 py-2 rounded-lg transition-all ${
                      isActive
                        ? "bg-amber-500 text-white"
                        : isCompleted
                        ? "bg-amber-500/20 text-amber-400"
                        : "bg-[#0a1628] text-slate-500"
                    } ${isSubStep ? "text-sm" : ""}`}
                  >
                    <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center ${
                      isActive || isCompleted ? "bg-amber-500/30" : "bg-slate-700"
                    }`}>
                      {isCompleted ? <Check size={14} /> : <step.icon size={14} />}
                    </div>
                    <span className={`font-medium hidden ${isSubStep ? "md:inline" : "sm:inline"}`}>
                      {step.name}
                    </span>
                  </button>
                  {showSeparator && (
                    <ChevronRight size={16} className="mx-0.5 sm:mx-1 text-slate-600 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>
          {/* Sub-step indicator */}
          {(currentStep === "3a" || currentStep === "3b" || currentStep === "3c") && (
            <div className="flex items-center justify-center gap-2 mt-2 text-xs text-slate-400">
              <span>MTM Order:</span>
              <span className={currentStep === "3a" ? "text-amber-400 font-medium" : ""}>Fabric</span>
              <span>→</span>
              <span className={currentStep === "3b" ? "text-amber-400 font-medium" : ""}>Measurements</span>
              <span>→</span>
              <span className={currentStep === "3c" ? "text-amber-400 font-medium" : ""}>Styles</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="pos-page-content max-w-6xl mx-auto p-4">
        <AnimatePresence mode="wait">
          {/* Step 1: Customer Selection */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold mb-2">Select Customer</h2>
                <p className="text-slate-400">Choose an existing customer or create new</p>
              </div>

              {selectedClient ? (
                <div className="max-w-lg mx-auto bg-[#111c2e] rounded-xl p-6 border border-amber-500/50">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-medium text-slate-400">Selected Customer</h3>
                    <button onClick={() => setSelectedClient(null)} className="text-slate-400 hover:text-red-400">
                      <X size={18} />
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-2xl font-bold">
                      {selectedClient.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="text-xl font-bold">{selectedClient.name}</p>
                      {selectedClient.email && <p className="text-slate-400 flex items-center gap-2"><Mail size={14} />{selectedClient.email}</p>}
                      {selectedClient.phone && <p className="text-slate-400 flex items-center gap-2"><Phone size={14} />{selectedClient.phone}</p>}
                    </div>
                  </div>
                </div>
              ) : showNewClientForm ? (
                <div className="max-w-lg mx-auto bg-[#111c2e] rounded-xl p-6 border border-white/10">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold flex items-center gap-2"><UserPlus size={18} className="text-amber-400" />New Customer</h3>
                    <button onClick={() => setShowNewClientForm(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Name *</label>
                      <Input placeholder="Customer name" value={newClientForm.name} onChange={(e) => setNewClientForm({...newClientForm, name: e.target.value})} className="bg-[#0a1628] border-white/10" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Email</label>
                      <Input type="email" placeholder="email@example.com" value={newClientForm.email} onChange={(e) => setNewClientForm({...newClientForm, email: e.target.value})} className="bg-[#0a1628] border-white/10" />
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Phone</label>
                      <Input placeholder="Phone number" value={newClientForm.phone} onChange={(e) => setNewClientForm({...newClientForm, phone: e.target.value})} className="bg-[#0a1628] border-white/10" />
                    </div>
                    <Button onClick={createNewClient} disabled={creatingClient || !newClientForm.name.trim()} className="w-full bg-amber-500 hover:bg-amber-600">
                      {creatingClient ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Save size={16} className="mr-2" />}
                      Create Customer
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="max-w-lg mx-auto space-y-4">
                  <div className="relative">
                    <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input placeholder="Search by name, email, or phone..." value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} className="pl-12 h-14 text-lg bg-[#111c2e] border-white/10" />
                  </div>
                  
                  <div className="grid gap-2 max-h-[400px] overflow-y-auto">
                    {filteredClients.map(client => (
                      <button key={client.id} onClick={() => selectClient(client)} className="w-full p-4 bg-[#111c2e] rounded-lg border border-white/10 hover:border-amber-500/50 text-left flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center text-lg font-bold">{client.name?.charAt(0)}</div>
                        <div className="flex-1">
                          <p className="font-medium">{client.name}</p>
                          <p className="text-sm text-slate-400">{client.email || client.phone}</p>
                        </div>
                        <ChevronRight size={20} className="text-slate-500" />
                      </button>
                    ))}
                  </div>

                  <Button onClick={() => setShowNewClientForm(true)} variant="outline" className="w-full h-12 border-amber-500/50 text-amber-400">
                    <UserPlus size={18} className="mr-2" />New Customer
                  </Button>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 2: Customer Profile */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">Customer Profile & Order Setup</h2>
                <p className="text-slate-400">Review customer history and select garment type</p>
              </div>

              {loadingProfile ? (
                <div className="text-center py-12">
                  <Loader2 size={32} className="animate-spin mx-auto text-amber-400" />
                  <p className="mt-2 text-slate-400">Loading profile...</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Customer Info */}
                  <div className="bg-[#111c2e] rounded-xl p-6 border border-white/10">
                    <h3 className="font-bold mb-4 flex items-center gap-2"><User size={18} className="text-amber-400" />Customer Info</h3>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-2xl font-bold">
                        {selectedClient?.name?.charAt(0)}
                      </div>
                      <div>
                        <p className="text-xl font-bold">{selectedClient?.name}</p>
                        {selectedClient?.email && <p className="text-sm text-slate-400">{selectedClient?.email}</p>}
                        {selectedClient?.phone && <p className="text-sm text-slate-400">{selectedClient?.phone}</p>}
                      </div>
                    </div>
                    <button onClick={() => setCurrentStep(1)} className="text-blue-400 hover:text-blue-300 text-sm">
                      <Edit2 size={14} className="inline mr-1" />Change Customer
                    </button>
                  </div>

                  {/* Saved Measurements */}
                  <div className="bg-[#111c2e] rounded-xl p-6 border border-white/10">
                    <h3 className="font-bold mb-4 flex items-center gap-2"><Ruler size={18} className="text-amber-400" />Saved Measurements</h3>
                    {clientMeasurements ? (
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between"><span className="text-slate-400">Chest</span><span>{clientMeasurements.chest || "-"}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Waist</span><span>{clientMeasurements.waist || "-"}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Seat</span><span>{clientMeasurements.seat || "-"}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Sleeve</span><span>{clientMeasurements.sleeve || "-"}</span></div>
                        <div className="flex justify-between"><span className="text-slate-400">Inseam</span><span>{clientMeasurements.inseam || "-"}</span></div>
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No saved measurements</p>
                    )}
                  </div>

                  {/* Garment Type Selection - REQUIRED */}
                  <div className="lg:col-span-2 bg-[#111c2e] rounded-xl p-6 border border-amber-500/30">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Shirt size={18} className="text-amber-400" />
                      Select Garment Type *
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {GARMENT_TYPES.map(g => (
                        <button
                          key={g.value}
                          onClick={() => handleGarmentTypeChange(g.value)}
                          className={`p-4 rounded-lg border text-center transition-all ${
                            garmentType === g.value
                              ? "bg-amber-500/20 border-amber-500"
                              : "bg-[#0a1628] border-white/10 hover:border-amber-500/50"
                          }`}
                        >
                          <p className="font-medium">{g.label}</p>
                        </button>
                      ))}
                    </div>
                    {!garmentType && (
                      <p className="text-amber-400 text-sm mt-2">Please select a garment type to continue</p>
                    )}
                  </div>

                  {/* Construction Type Selection */}
                  <div className="lg:col-span-2 bg-[#111c2e] rounded-xl p-6 border border-white/10">
                    <h3 className="font-bold mb-4 flex items-center gap-2">
                      <Scissors size={18} className="text-blue-400" />
                      Construction Type
                    </h3>
                    {/* Make toggle — picks the pricing pipeline. Switching
                        resets the construction selection to the make's
                        default so the user can't accidentally save a CTD
                        order priced through the RC pipeline. */}
                    <div className="mb-3">
                      <MakeToggle
                        value={make}
                        onChange={(next) => {
                          setMake(next);
                          setConstructionType(MAKE_PROFILES[next].defaultConstruction);
                        }}
                      />
                    </div>
                    {(() => {
                      // Decide which construction list to render based on the profile, not the make id.
                      // RC make + CTD-fabric/RC-make hybrid → RC_LABOR; CTD make + RC-fabric/CTD-make hybrid → CTD_LABOR.
                      const usesRCConstructions = !MAKE_PROFILES[make].defaultConstruction.startsWith("CTD");
                      return (
                    <div className="space-y-3">
                      <div>
                        <p className={`text-xs mb-2 ${usesRCConstructions ? "" : "text-slate-600"}`} style={usesRCConstructions ? { color: MAKE_PROFILES[make].color } : undefined}>
                          {usesRCConstructions ? `RC Construction (${MAKE_PROFILES[make].label}):` : `RC Construction (${MAKE_PROFILES[make].label} active — hidden)`}
                        </p>
                        {usesRCConstructions && (
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(RC_LABOR).map(type => (
                            <button
                              key={type}
                              data-testid={`construction-${type.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => setConstructionType(type)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                constructionType === type ? "text-white" : "bg-[#0a1628] text-slate-400 hover:text-white border border-white/10"
                              }`}
                              style={constructionType === type ? { backgroundColor: MAKE_PROFILES[make].color } : undefined}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                        )}
                      </div>
                      <div>
                        <p className={`text-xs mb-2 ${!usesRCConstructions ? "" : "text-slate-600"}`} style={!usesRCConstructions ? { color: MAKE_PROFILES[make].color } : undefined}>
                          {!usesRCConstructions ? `CTD Construction (${MAKE_PROFILES[make].label}):` : `CTD Construction (${MAKE_PROFILES[make].label} active — hidden)`}
                        </p>
                        {!usesRCConstructions && (
                        <div className="flex flex-wrap gap-2">
                          {Object.keys(CTD_LABOR).map(type => (
                            <button
                              key={type}
                              data-testid={`construction-${type.toLowerCase().replace(/\s+/g, '-')}`}
                              onClick={() => setConstructionType(type)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                constructionType === type ? "text-white" : "bg-[#0a1628] text-slate-400 hover:text-white border border-white/10"
                              }`}
                              style={constructionType === type ? { backgroundColor: MAKE_PROFILES[make].color } : undefined}
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                        )}
                      </div>
                    </div>
                      );
                    })()}
                    <div className="mt-3 flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                      <span>Selected:</span>
                      <span
                        className="font-medium"
                        style={{ color: MAKE_PROFILES[make].color }}
                      >
                        {constructionType}
                      </span>
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-400">
                        {MAKE_PROFILES[make].markup}× markup · {MAKE_PROFILES[make].leadWeeks}-wk lead time
                        {MAKE_PROFILES[make].rcShippingPerGarment > 0 && ` · +$${MAKE_PROFILES[make].rcShippingPerGarment} shipping`}
                        {MAKE_PROFILES[make].bankFeePct > 0 && ` · +${(MAKE_PROFILES[make].bankFeePct*100)}% bank fee`}
                      </span>
                      {fabricSelection && (
                        <>
                          <span className="text-slate-600">·</span>
                          <span className="text-emerald-400 font-medium">
                            ${calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)} estimated
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Order History */}
                  <div className="lg:col-span-2 bg-[#111c2e] rounded-xl p-6 border border-white/10">
                    <h3 className="font-bold mb-4 flex items-center gap-2"><History size={18} className="text-amber-400" />Order History</h3>
                    {clientOrders.length > 0 ? (
                      <div className="space-y-2">
                        {clientOrders.slice(0, 5).map(order => (
                          <div key={order.id} className="flex items-center justify-between py-2 border-b border-white/5">
                            <div>
                              <p className="font-medium">{order.order_number}</p>
                              <p className="text-sm text-slate-400">{order.garment_type}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm">{format(new Date(order.order_date || order.created_at), "MMM d, yyyy")}</p>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                order.status === "completed" ? "bg-green-500/20 text-green-400" :
                                order.status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
                                "bg-blue-500/20 text-blue-400"
                              }`}>{order.status}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-slate-500 text-sm">No previous orders</p>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Step 3a: Fabric & Lining */}
          {currentStep === "3a" && (
            <motion.div
              key="step3a"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <MTMFabricStep
                fabricSelection={fabricSelection}
                setFabricSelection={setFabricSelection}
                liningSelection={liningSelection}
                setLiningSelection={setLiningSelection}
                liningType={liningType}
                setLiningType={setLiningType}
                pipingSelection={pipingSelection}
                setPipingSelection={setPipingSelection}
                jacketButtonSelection={jacketButtonSelection}
                setJacketButtonSelection={setJacketButtonSelection}
                vestButtonSelection={vestButtonSelection}
                setVestButtonSelection={setVestButtonSelection}
                vestMatchesJacket={vestMatchesJacket}
                setVestMatchesJacket={setVestMatchesJacket}
                includeJacket={includeJacket}
                includeTrouser={includeTrouser}
                includeVest={includeVest}
                // Per-garment Quick-Order-style pickers (new)
                garmentType={garmentType}
                fabricByGarment={fabricByGarment}
                setFabricByGarment={setFabricByGarment}
                liningByGarment={liningByGarment}
                setLiningByGarment={setLiningByGarment}
                buttonByGarment={buttonByGarment}
                setButtonByGarment={setButtonByGarment}
              />
            </motion.div>
          )}

          {/* Step 3b: Measurements */}
          {currentStep === "3b" && (
            <motion.div
              key="step3b"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <MTMMeasurementsStep
                includeJacket={includeJacket}
                includeTrouser={includeTrouser}
                includeVest={includeVest}
                sizeCharts={sizeCharts}
                // Jacket
                jacketSize={jacketSize}
                setJacketSize={setJacketSize}
                jacketStartingSpecs={jacketStartingSpecs}
                setJacketStartingSpecs={setJacketStartingSpecs}
                jacketAdjustments={jacketAdjustments}
                setJacketAdjustments={setJacketAdjustments}
                jacketPosture={jacketPosture}
                setJacketPosture={setJacketPosture}
                jacketRemarks={jacketRemarks}
                setJacketRemarks={setJacketRemarks}
                // Trouser
                trouserSize={trouserSize}
                setTrouserSize={setTrouserSize}
                trouserStartingSpecs={trouserStartingSpecs}
                setTrouserStartingSpecs={setTrouserStartingSpecs}
                trouserAdjustments={trouserAdjustments}
                setTrouserAdjustments={setTrouserAdjustments}
                trouserPosture={trouserPosture}
                setTrouserPosture={setTrouserPosture}
                trouserRemarks={trouserRemarks}
                setTrouserRemarks={setTrouserRemarks}
                // Vest
                vestSize={vestSize}
                setVestSize={setVestSize}
                vestStartingSpecs={vestStartingSpecs}
                setVestStartingSpecs={setVestStartingSpecs}
                vestAdjustments={vestAdjustments}
                setVestAdjustments={setVestAdjustments}
                vestRemarks={vestRemarks}
                setVestRemarks={setVestRemarks}
              />
            </motion.div>
          )}

          {/* Step 3c: Styles */}
          {currentStep === "3c" && (
            <motion.div
              key="step3c"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="flex gap-4 2xl:grid 2xl:grid-cols-[14rem_minmax(0,1fr)_22rem] 2xl:gap-6"
            >
              {/* Left rail — Workshop View nav (≥2xl only) */}
              <GarmentJumpNav
                includes={{ jacket: includeJacket, trouser: includeTrouser, vest: includeVest, shirt: includeShirt }}
                make={make}
                constructionType={constructionType}
                onJump={(id) => {
                  const el = document.getElementById(`pos-styling-${id}`);
                  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
              />

              {/* Main Styling Form */}
              <div className={`flex-1 min-w-0 ${showMockupPreview ? 'pr-4' : ''}`}>
                {/* Style Preview Button */}
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setShowMockupPreview(!showMockupPreview)}
                    disabled={!garmentType}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                      showMockupPreview 
                        ? 'bg-amber-500/20 border border-amber-500/50 text-amber-400' 
                        : 'bg-[#0a1628] border border-white/10 text-white hover:border-amber-500/30'
                    } ${!garmentType ? 'opacity-50 cursor-not-allowed' : ''}`}
                    data-testid="style-preview-btn"
                  >
                    <Wand2 size={16} />
                    Style Preview
                  </button>
                </div>
                
                <MTMStylesStep
                  includeJacket={includeJacket}
                  includeTrouser={includeTrouser}
                  includeVest={includeVest}
                  includeShirt={includeShirt}
                  garmentType={garmentType}
                  // Jacket styling
                  jacketStyling={jacketStyling}
                  setJacketStyling={setJacketStyling}
                  jacketStylingRemarks={jacketStylingRemarks}
                  setJacketStylingRemarks={setJacketStylingRemarks}
                  // Pant styling
                  pantStyling={pantStyling}
                  setPantStyling={setPantStyling}
                  pantStylingRemarks={pantStylingRemarks}
                  setPantStylingRemarks={setPantStylingRemarks}
                  // Vest styling
                  vestStyling={vestStyling}
                  setVestStyling={setVestStyling}
                  vestStylingRemarks={vestStylingRemarks}
                  setVestStylingRemarks={setVestStylingRemarks}
                  // Shirt styling
                  shirtStyling={shirtStyling}
                  setShirtStyling={setShirtStyling}
                  shirtStylingRemarks={shirtStylingRemarks}
                  setShirtStylingRemarks={setShirtStylingRemarks}
                  // Order options
                  rushOrder={rushOrder}
                  setRushOrder={setRushOrder}
                  orderNotes={orderNotes}
                  setOrderNotes={setOrderNotes}
                />
              </div>
              
              {/* Mockup Preview Side Panel - Only show when Style Preview button is clicked */}
              {showMockupPreview && garmentType && (
                <SuitMockupPreview
                  garmentType={GARMENT_TYPES.find(g => g.value === garmentType)?.label || garmentType}
                  jacketStyling={jacketStyling}
                  pantStyling={pantStyling}
                  vestStyling={vestStyling}
                  shirtStyling={shirtStyling}
                  fabricColor={fabricSelection?.name || fabricSelection?.color || "Navy Blue"}
                  fabricPattern={fabricSelection?.pattern || "Solid"}
                  isExpanded={showMockupPreview}
                  onToggle={() => setShowMockupPreview(!showMockupPreview)}
                />
              )}

              {/* Right rail — Style Cart (≥2xl only, hidden when Mockup Preview is active) */}
              {!showMockupPreview && (
                <StyleCartPanel
                  jacketStyling={jacketStyling}
                  pantStyling={pantStyling}
                  vestStyling={vestStyling}
                  shirtStyling={shirtStyling}
                  includes={{ jacket: includeJacket, trouser: includeTrouser, vest: includeVest, shirt: includeShirt }}
                  fabricSelection={fabricSelection}
                  estimatedPrice={fabricSelection ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType) : null}
                  make={make}
                  constructionType={constructionType}
                />
              )}
            </motion.div>
          )}

          {/* Step 4: Summary */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-3xl mx-auto"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">Order Summary</h2>
                <p className="text-slate-400">Review all details before payment</p>
              </div>

              <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                {/* Customer */}
                <div className="p-4 border-b border-white/10 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
                      {selectedClient?.name?.charAt(0)}
                    </div>
                    <div>
                      <p className="font-medium">{selectedClient?.name}</p>
                      <p className="text-sm text-slate-400">{selectedClient?.email || selectedClient?.phone}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-3 py-1 rounded-full ${rushOrder ? "bg-amber-500/20 text-amber-400" : "bg-slate-700 text-slate-400"}`}>
                    {rushOrder ? "🚨 Rush Order" : "Standard"}
                  </span>
                </div>

                {/* Order Details - Items */}
                <div className="p-4 border-b border-white/10">
                  <div className="flex justify-between items-center mb-3">
                    <p className="text-sm font-medium text-slate-400">ORDER ITEMS</p>
                    <button
                      onClick={saveCurrentItemAndAddNew}
                      className="flex items-center gap-1 text-xs px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                    >
                      <Plus size={14} />
                      Add New Item
                    </button>
                  </div>
                  
                  {/* Saved Items */}
                  {savedItems.length > 0 && (
                    <div className="space-y-2 mb-3">
                      {savedItems.map((item, idx) => (
                        <div key={item.id} className="bg-[#0a1628] p-3 rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs bg-slate-700 px-2 py-0.5 rounded font-medium">#{idx + 1}</span>
                                <span className="font-medium">{item.garmentLabel}</span>
                              </div>
                              {item.fabricSelection && (
                                <p className="text-xs text-slate-400 mt-1">
                                  <span className="text-amber-400 font-mono">{item.fabricSelection.rc_code}</span>
                                  <span className="mx-1">•</span>
                                  <span>{item.fabricSelection.composition || item.fabricSelection.color}</span>
                                </p>
                              )}
                              {item.constructionType && (
                                <p className="text-xs text-slate-500 mt-0.5">
                                  <span className={item.constructionType.startsWith('CTD') ? 'text-teal-400' : 'text-blue-400'}>{item.constructionType}</span>
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-amber-400 text-lg">${item.price}</span>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button 
                                    className="text-slate-400 hover:text-white p-1 rounded hover:bg-slate-700 transition-colors"
                                    data-testid={`item-menu-${idx}`}
                                  >
                                    <MoreVertical size={16} />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="bg-[#1a2744] border-white/10 text-white w-52">
                                  {/* Edit Options */}
                                  <DropdownMenuItem 
                                    onClick={() => editSavedItem(item.id)}
                                    className="hover:bg-white/10 cursor-pointer"
                                    data-testid={`edit-item-${idx}`}
                                  >
                                    <Edit2 size={14} className="mr-2 text-blue-400" />
                                    Edit Full Order
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => editSavedItemFabric(item.id)}
                                    className="hover:bg-white/10 cursor-pointer text-sm"
                                    data-testid={`edit-fabric-${idx}`}
                                  >
                                    <span className="w-5 h-5 mr-2 flex items-center justify-center text-purple-400">◈</span>
                                    Edit Fabric & Lining
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => editSavedItemMeasurements(item.id)}
                                    className="hover:bg-white/10 cursor-pointer text-sm"
                                    data-testid={`edit-measurements-${idx}`}
                                  >
                                    <Ruler size={14} className="mr-2 text-cyan-400" />
                                    Edit Measurements
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => editSavedItemStyles(item.id)}
                                    className="hover:bg-white/10 cursor-pointer text-sm"
                                    data-testid={`edit-styles-${idx}`}
                                  >
                                    <Scissors size={14} className="mr-2 text-rose-400" />
                                    Edit Styles
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-white/10" />
                                  <DropdownMenuItem 
                                    onClick={() => copyWholeOrder(item.id)}
                                    className="hover:bg-white/10 cursor-pointer"
                                    data-testid={`copy-item-${idx}`}
                                  >
                                    <Copy size={14} className="mr-2" />
                                    Duplicate Whole Order
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => copyOrderStyle(item.id)}
                                    className="hover:bg-white/10 cursor-pointer"
                                    data-testid={`copy-style-${idx}`}
                                  >
                                    <Scissors size={14} className="mr-2" />
                                    Duplicate Styles Only
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => copyOrderMeasurements(item.id)}
                                    className="hover:bg-white/10 cursor-pointer"
                                    data-testid={`copy-measurements-${idx}`}
                                  >
                                    <Ruler size={14} className="mr-2" />
                                    Duplicate Measurements Only
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator className="bg-white/10" />
                                  <DropdownMenuItem 
                                    onClick={() => removeSavedItem(item.id)}
                                    className="hover:bg-red-500/20 text-red-400 cursor-pointer"
                                    data-testid={`remove-item-${idx}`}
                                  >
                                    <Trash2 size={14} className="mr-2" />
                                    Remove Item
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                          
                          {/* Item Details */}
                          <div className="grid grid-cols-3 gap-2 mt-2 pt-2 border-t border-white/5 text-xs">
                            {item.jacketSize && (
                              <div>
                                <span className="text-slate-500">Jacket:</span>
                                <span className="ml-1 text-white">{item.jacketSize}</span>
                              </div>
                            )}
                            {item.trouserSize && (
                              <div>
                                <span className="text-slate-500">Trouser:</span>
                                <span className="ml-1 text-white">{item.trouserSize}</span>
                              </div>
                            )}
                            {item.vestSize && (
                              <div>
                                <span className="text-slate-500">Vest:</span>
                                <span className="ml-1 text-white">{item.vestSize}</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Styling Summary */}
                          {item.jacketStyling && (
                            <div className="mt-2 pt-2 border-t border-white/5 text-xs">
                              <span className="text-slate-500">Style:</span>
                              <span className="ml-1 text-white">
                                {item.jacketStyling.lapel_type} • {item.jacketStyling.front_closure} • {item.jacketStyling.vent}
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* Current Item */}
                  {garmentType && (
                    <div className="space-y-2">
                      <div className="bg-amber-500/10 p-3 rounded-lg border border-amber-500/30">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs bg-amber-500/30 text-amber-400 px-2 py-0.5 rounded font-medium">Current</span>
                              <span className="font-medium">{GARMENT_TYPES.find(g => g.value === garmentType)?.label}</span>
                            </div>
                            {fabricSelection && (
                              <p className="text-xs text-slate-400 ml-0 mt-1">
                                <span className="text-amber-400 font-mono">{fabricSelection.rc_code}</span>
                                <span className="mx-1">•</span>
                                <span>{fabricSelection.composition}</span>
                                <span className="mx-1">•</span>
                                <span>${fabricSelection.price}/m</span>
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-amber-400 text-lg">
                              ${fabricSelection?.price 
                                ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
                                : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0
                              }
                            </span>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button 
                                  className="text-slate-400 hover:text-white p-1 rounded hover:bg-white/10 transition-colors"
                                  data-testid="current-item-menu"
                                >
                                  <MoreVertical size={16} />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#1a2744] border-white/10 text-white w-52">
                                {/* Edit Options for Current Item */}
                                <DropdownMenuItem 
                                  onClick={() => editCurrentItemSection(2)}
                                  className="hover:bg-white/10 cursor-pointer"
                                  data-testid="edit-current-profile"
                                >
                                  <Edit2 size={14} className="mr-2 text-blue-400" />
                                  Edit Garment Type
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => editCurrentItemSection("3a")}
                                  className="hover:bg-white/10 cursor-pointer text-sm"
                                  data-testid="edit-current-fabric"
                                >
                                  <span className="w-5 h-5 mr-2 flex items-center justify-center text-purple-400">◈</span>
                                  Edit Fabric & Lining
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => editCurrentItemSection("3b")}
                                  className="hover:bg-white/10 cursor-pointer text-sm"
                                  data-testid="edit-current-measurements"
                                >
                                  <Ruler size={14} className="mr-2 text-cyan-400" />
                                  Edit Measurements
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => editCurrentItemSection("3c")}
                                  className="hover:bg-white/10 cursor-pointer text-sm"
                                  data-testid="edit-current-styles"
                                >
                                  <Scissors size={14} className="mr-2 text-rose-400" />
                                  Edit Styles
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    saveCurrentItemAndAddNew();
                                  }}
                                  className="hover:bg-white/10 cursor-pointer"
                                  data-testid="save-and-new"
                                >
                                  <Plus size={14} className="mr-2 text-emerald-400" />
                                  Save & Add New Item
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    // Copy current item as duplicate (whole order)
                                    const currentPrice = fabricSelection?.price 
                                      ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
                                      : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0;
                                    const newItem = {
                                      id: Date.now(),
                                      garmentType,
                                      garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
                                      price: currentPrice,
                                      fabricSelection: fabricSelection ? { ...fabricSelection } : null,
                                      liningSelection: liningSelection ? { ...liningSelection } : null,
                                      liningType,
                                      jacketSize,
                                      jacketStartingSpecs: { ...jacketStartingSpecs },
                                      jacketAdjustments: { ...jacketAdjustments },
                                      jacketPosture: { ...jacketPosture },
                                      jacketStyling: { ...jacketStyling },
                                      jacketRemarks,
                                      jacketStylingRemarks,
                                      trouserSize,
                                      trouserStartingSpecs: { ...trouserStartingSpecs },
                                      trouserAdjustments: { ...trouserAdjustments },
                                      trouserPosture: { ...trouserPosture },
                                      pantStyling: { ...pantStyling },
                                      trouserRemarks,
                                      pantStylingRemarks,
                                      vestSize,
                                      vestStartingSpecs: { ...vestStartingSpecs },
                                      vestAdjustments: { ...vestAdjustments },
                                      vestStyling: { ...vestStyling },
                                      vestRemarks,
                                      vestStylingRemarks,
                                      includeJacket,
                                      includeTrouser,
                                      includeVest
                                    };
                                    setSavedItems(prev => [...prev, newItem]);
                                    toast.success(`Duplicated ${newItem.garmentLabel}`);
                                  }}
                                  className="hover:bg-white/10 cursor-pointer"
                                  data-testid="duplicate-current"
                                >
                                  <Copy size={14} className="mr-2" />
                                  Duplicate Whole Order
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    // Copy current item with styles only (measurements reset)
                                    const currentPrice = fabricSelection?.price 
                                      ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
                                      : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0;
                                    const defaultJacketAdjustments = { chest: 0, stomach: 0, seat: 0, shoulder: 0, bicep: 0, right_sleeve_length: 0, left_sleeve_length: 0, back_length: 0, front_length: 0, right_cuff: 0, left_cuff: 0, button_stance: 0 };
                                    const defaultJacketPosture = { stoop: 0, erect: 0, high_shoulder: 0, sloping_shoulder: 0 };
                                    const defaultTrouserAdjustments = { waist: 0, seat: 0, urise: 0, thigh: 0, right_pant_length: 0, left_pant_length: 0, knee: 0, bottom: 0 };
                                    const defaultTrouserPosture = { front_rise: 0, back_rise: 0 };
                                    const defaultVestAdjustments = { chest: 0, stomach: 0, back_length: 0, front_length: 0, bottom: 0 };
                                    const newItem = {
                                      id: Date.now(),
                                      garmentType,
                                      garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
                                      price: currentPrice,
                                      fabricSelection: fabricSelection ? { ...fabricSelection } : null,
                                      liningSelection: liningSelection ? { ...liningSelection } : null,
                                      liningType,
                                      // Keep styling
                                      jacketStyling: { ...jacketStyling },
                                      pantStyling: { ...pantStyling },
                                      vestStyling: { ...vestStyling },
                                      // Reset measurements
                                      jacketSize: "",
                                      jacketAdjustments: { ...defaultJacketAdjustments },
                                      jacketPosture: { ...defaultJacketPosture },
                                      trouserSize: "",
                                      trouserAdjustments: { ...defaultTrouserAdjustments },
                                      trouserPosture: { ...defaultTrouserPosture },
                                      vestSize: "",
                                      vestAdjustments: { ...defaultVestAdjustments },
                                      includeJacket,
                                      includeTrouser,
                                      includeVest
                                    };
                                    setSavedItems(prev => [...prev, newItem]);
                                    toast.success(`Duplicated ${newItem.garmentLabel} with styles only`);
                                  }}
                                  className="hover:bg-white/10 cursor-pointer"
                                  data-testid="duplicate-styles"
                                >
                                  <Scissors size={14} className="mr-2" />
                                  Duplicate Styles Only
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => {
                                    // Copy current item with measurements only (styles reset)
                                    const currentPrice = fabricSelection?.price 
                                      ? calculateGarmentPrice(fabricSelection.price, garmentType, constructionType)
                                      : GARMENT_TYPES.find(g => g.value === garmentType)?.price || 0;
                                    const defaultJacketStyling = {
                                      canvas_construction: "Half Canvas", front_closure: "2-Button", lapel_type: "Notch",
                                      lapel_width: "3\" (7.6cm)", gorge_line: "Standard", waist_pocket_type: "Flap", waist_pocket_angle: "Straight",
                                      ticket_pocket: "None", chest_pocket: "Welt", button_style: "Horn",
                                      exterior_stitch: "None", sleeve_buttonholes: "4 Kissing", vent: "Double Vent",
                                      lapel_buttonhole: "None", under_collar_monogram: "",
                                      lining_color: "", sleeve_lining_color: ""
                                    };
                                    const defaultPantStyling = {
                                      body_style: "Regular", front_style: "Flat Front", front_pocket: "Slant",
                                      back_pocket_closure: "Button", bottom_finishing: "Plain Hem",
                                      back_pocket_style: "Double Besom", front_closure: "Button & Tab",
                                      waistband_style: "Standard", waistband_width: "1.5\" (3.8cm)",
                                      pleat_depth: "—", watch_pocket: "None", inner_watch_pocket: "None",
                                      front_pocket_bartack: "None", front_pocket_stitch: "None",
                                      pant_lining_color: ""
                                    };
                                    const defaultVestStyling = {
                                      lapel: "None", front_closure: "5-Button", pocket_type: "Welt",
                                      pocket_number: "4 Pockets", back: "Lining"
                                    };
                                    const newItem = {
                                      id: Date.now(),
                                      garmentType,
                                      garmentLabel: GARMENT_TYPES.find(g => g.value === garmentType)?.label,
                                      price: currentPrice,
                                      fabricSelection: fabricSelection ? { ...fabricSelection } : null,
                                      liningSelection: liningSelection ? { ...liningSelection } : null,
                                      liningType,
                                      // Reset styling
                                      jacketStyling: { ...defaultJacketStyling },
                                      pantStyling: { ...defaultPantStyling },
                                      vestStyling: { ...defaultVestStyling },
                                      // Keep measurements
                                      jacketSize,
                                      jacketStartingSpecs: { ...jacketStartingSpecs },
                                      jacketAdjustments: { ...jacketAdjustments },
                                      jacketPosture: { ...jacketPosture },
                                      trouserSize,
                                      trouserStartingSpecs: { ...trouserStartingSpecs },
                                      trouserAdjustments: { ...trouserAdjustments },
                                      trouserPosture: { ...trouserPosture },
                                      vestSize,
                                      vestStartingSpecs: { ...vestStartingSpecs },
                                      vestAdjustments: { ...vestAdjustments },
                                      includeJacket,
                                      includeTrouser,
                                      includeVest
                                    };
                                    setSavedItems(prev => [...prev, newItem]);
                                    toast.success(`Duplicated ${newItem.garmentLabel} with measurements only`);
                                  }}
                                  className="hover:bg-white/10 cursor-pointer"
                                  data-testid="duplicate-measurements"
                                >
                                  <Ruler size={14} className="mr-2" />
                                  Duplicate Measurements Only
                                </DropdownMenuItem>
                                <DropdownMenuSeparator className="bg-white/10" />
                                <DropdownMenuItem 
                                  onClick={() => {
                                    resetItemForNew();
                                    toast.info("Item cleared");
                                  }}
                                  className="hover:bg-red-500/20 text-red-400 cursor-pointer"
                                  data-testid="clear-current"
                                >
                                  <Trash2 size={14} className="mr-2" />
                                  Clear Item
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                        
                        {/* Item Details */}
                        <div className="grid grid-cols-3 gap-2 mt-3 pt-2 border-t border-amber-500/20 text-xs">
                          {includeJacket && jacketSize && (
                            <div>
                              <span className="text-slate-500">Jacket:</span>
                              <span className="ml-1 text-white">{jacketSize}</span>
                            </div>
                          )}
                          {includeTrouser && trouserSize && (
                            <div>
                              <span className="text-slate-500">Trouser:</span>
                              <span className="ml-1 text-white">{trouserSize}</span>
                            </div>
                          )}
                          {includeVest && vestSize && (
                            <div>
                              <span className="text-slate-500">Vest:</span>
                              <span className="ml-1 text-white">{vestSize}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Styling Summary */}
                        {includeJacket && jacketStyling && (
                          <div className="mt-2 pt-2 border-t border-amber-500/20 text-xs">
                            <span className="text-slate-500">Style:</span>
                            <span className="ml-1 text-white">
                              {jacketStyling.lapel_type} • {jacketStyling.front_closure} • {jacketStyling.vent}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Order Fees */}
                  <div className="mt-3 pt-3 border-t border-white/10 space-y-2">
                    {rushOrder && (
                      <div className="flex justify-between text-amber-400 text-sm">
                        <span>Rush Order Fee</span>
                        <span>$100</span>
                      </div>
                    )}
                    {discount > 0 && (
                      <div className="flex justify-between text-green-400 text-sm">
                        <span>Discount ({discountType === "percent" ? `${discount}%` : "Fixed"})</span>
                        <span>-${totals.discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Fabric & Lining */}
                <div className="p-4 border-b border-white/10">
                  <p className="text-sm font-medium text-slate-400 mb-2">FABRIC & LINING</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">Fabric:</span>
                      <span className="ml-2 text-amber-400 font-mono">{fabricSelection?.rc_code || "Not selected"}</span>
                      {fabricSelection && (
                        <p className="text-xs text-slate-500 mt-1">{fabricSelection.composition} • {fabricSelection.color}</p>
                      )}
                    </div>
                    <div>
                      <span className="text-slate-500">Lining:</span>
                      <span className="ml-2 text-blue-400 font-mono">{liningSelection?.rc_code || "Not selected"}</span>
                      {liningSelection && (
                        <p className="text-xs text-slate-500 mt-1">{liningType}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sizing & Measurements */}
                <div className="p-4 border-b border-white/10">
                  <p className="text-sm font-medium text-slate-400 mb-2">SIZING & MEASUREMENTS</p>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    {includeJacket && (
                      <div className="bg-[#0a1628] p-3 rounded-lg">
                        <p className="font-medium text-amber-400 mb-1">Jacket</p>
                        <p className="text-xs">Size: {jacketSize || "-"}</p>
                        {Object.entries(jacketAdjustments).some(([k, v]) => v !== 0) && (
                          <p className="text-xs text-slate-500 mt-1">
                            Adjustments: {Object.entries(jacketAdjustments)
                              .filter(([k, v]) => v !== 0)
                              .map(([k, v]) => `${k.replace('_', ' ')}: ${v > 0 ? '+' : ''}${v}`)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    {includeTrouser && (
                      <div className="bg-[#0a1628] p-3 rounded-lg">
                        <p className="font-medium text-blue-400 mb-1">Trouser</p>
                        <p className="text-xs">Size: {trouserSize || "-"}</p>
                        {Object.entries(trouserAdjustments).some(([k, v]) => v !== 0) && (
                          <p className="text-xs text-slate-500 mt-1">
                            Adjustments: {Object.entries(trouserAdjustments)
                              .filter(([k, v]) => v !== 0)
                              .map(([k, v]) => `${k.replace('_', ' ')}: ${v > 0 ? '+' : ''}${v}`)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                    {includeVest && (
                      <div className="bg-[#0a1628] p-3 rounded-lg">
                        <p className="font-medium text-purple-400 mb-1">Vest</p>
                        <p className="text-xs">Size: {vestSize || "-"}</p>
                        {Object.entries(vestAdjustments).some(([k, v]) => v !== 0) && (
                          <p className="text-xs text-slate-500 mt-1">
                            Adjustments: {Object.entries(vestAdjustments)
                              .filter(([k, v]) => v !== 0)
                              .map(([k, v]) => `${k.replace('_', ' ')}: ${v > 0 ? '+' : ''}${v}`)
                              .join(', ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Measurements & Posture */}
                {/* Posture Summary - only show if posture differs from normal */}
                {(jacketPosture?.left_shoulder && jacketPosture?.left_shoulder !== "normal") || 
                 (jacketPosture?.right_shoulder && jacketPosture?.right_shoulder !== "normal") ? (
                  <div className="p-4 border-b border-white/10">
                    <p className="text-sm font-medium text-slate-400 mb-2">POSTURE ADJUSTMENTS</p>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      {(jacketPosture?.left_shoulder && jacketPosture?.left_shoulder !== "normal") && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Left Shoulder Posture:</span>
                          <span className="text-green-400">{jacketPosture.left_shoulder.replace(/_/g, ' ')}</span>
                        </div>
                      )}
                      {(jacketPosture?.right_shoulder && jacketPosture?.right_shoulder !== "normal") && (
                        <div className="flex justify-between">
                          <span className="text-slate-500">Right Shoulder Posture:</span>
                          <span className="text-green-400">{jacketPosture.right_shoulder.replace(/_/g, ' ')}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}

                {/* Styling Summary */}
                <div className="p-4 border-b border-white/10">
                  <p className="text-sm font-medium text-slate-400 mb-2">STYLING</p>
                  <div className="space-y-2 text-xs">
                    {includeJacket && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-amber-400">Jacket:</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{jacketStyling.canvas_construction}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{jacketStyling.front_closure}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{jacketStyling.lapel_type} Lapel</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{jacketStyling.vent}</span>
                      </div>
                    )}
                    {includeTrouser && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-blue-400">Trouser:</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{pantStyling.body_style}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{pantStyling.front_style}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{pantStyling.bottom_finishing}</span>
                      </div>
                    )}
                    {includeVest && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-purple-400">Vest:</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{vestStyling.front_closure}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{vestStyling.pocket_type}</span>
                        <span className="px-2 py-0.5 bg-[#0a1628] rounded">{vestStyling.back}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {orderNotes && (
                  <div className="p-4 border-b border-white/10">
                    <p className="text-sm font-medium text-slate-400 mb-2">NOTES</p>
                    <p className="text-sm text-slate-300">{orderNotes}</p>
                  </div>
                )}

                {/* Pricing */}
                <div className="p-4 border-b border-white/10">
                  <p className="text-sm font-medium text-slate-400 mb-2">PRICING</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm text-slate-400">Custom Price:</span>
                    <Input
                      type="number"
                      placeholder="Use default"
                      value={customPrice}
                      onChange={(e) => setCustomPrice(e.target.value)}
                      className="w-32 h-8 text-sm bg-[#0a1628] border-white/10"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-400">Discount:</span>
                    <button onClick={() => setDiscountType(discountType === "fixed" ? "percent" : "fixed")} className="px-2 py-1 bg-[#0a1628] rounded text-xs">
                      {discountType === "fixed" ? "$" : "%"}
                    </button>
                    <Input
                      type="number"
                      value={discount || ""}
                      onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                      className="w-24 h-8 text-sm bg-[#0a1628] border-white/10"
                    />
                  </div>
                </div>

                {/* Totals */}
                <div className="p-4 bg-[#0a1628] space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-400">Subtotal</span>
                    <span>${totals.subtotal.toFixed(2)}</span>
                  </div>
                  {totals.rushFee > 0 && (
                    <div className="flex justify-between text-amber-400">
                      <span>Rush Fee</span>
                      <span>${totals.rushFee.toFixed(2)}</span>
                    </div>
                  )}
                  {totals.discountAmount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>Discount</span>
                      <span>-${totals.discountAmount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-slate-400">Tax ({taxRate}%)</span>
                    <span>${totals.taxAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t border-white/10">
                    <span>Total</span>
                    <span className="text-amber-400">${totals.total.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 5: Payment */}
          {currentStep === 5 && (
            <motion.div
              key="step5"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="max-w-5xl mx-auto"
            >
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">Payment & Invoice</h2>
                <p className="text-slate-400">Review invoice and complete payment</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Invoice Preview - Left Column */}
                <div className="lg:col-span-2 bg-white rounded-xl overflow-hidden shadow-lg">
                  {/* Invoice Header with Status */}
                  <div className="bg-[#111c2e] p-3 text-white flex items-center justify-between">
                    <h3 className="font-bold flex items-center gap-2">
                      <Receipt size={18} />Invoice Preview
                    </h3>
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                      totals.balanceDue <= 0 
                        ? "bg-emerald-500 text-white" 
                        : "bg-amber-500 text-white"
                    }`}>
                      {totals.balanceDue <= 0 ? "PAID" : "UNPAID"}
                    </div>
                  </div>
                  <div className="p-4 text-gray-900">
                    {/* Header */}
                    <div className="text-center mb-4 pb-3 border-b border-gray-200">
                      <h1 className="text-xl font-bold">LAGUERRE</h1>
                      <p className="text-xs text-gray-600">55 W Railroad Ave Building 24, Garnerville, NY 10923</p>
                      <p className="text-xs text-gray-600">hello@laguerre.co | www.laguerre.co</p>
                    </div>

                    {/* Bill To & Invoice Info */}
                    <div className="flex justify-between mb-4 text-sm">
                      <div>
                        <p className="text-xs text-gray-500 font-medium">BILL TO</p>
                        <p className="font-medium">{selectedClient?.name}</p>
                        {selectedClient?.email && <p className="text-gray-600 text-xs">{selectedClient.email}</p>}
                        {selectedClient?.phone && <p className="text-gray-600 text-xs">{selectedClient.phone}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500 font-medium">DATE</p>
                        <p className="text-gray-900">{format(new Date(), "MMM d, yyyy")}</p>
                        <p className="text-xs text-gray-500 mt-1">EST. ARRIVAL</p>
                        <p className="text-gray-900">{format(addWeeks(new Date(), rushOrder ? 2 : 4), "MMM d, yyyy")}</p>
                      </div>
                    </div>

                    {/* Items Table */}
                    <table className="w-full mb-4 text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200">
                          <th className="text-left py-2 text-xs text-gray-500">Item</th>
                          <th className="text-right py-2 text-xs text-gray-500">Price</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savedItems.map((item, idx) => (
                          <tr key={item.id} className="border-b border-gray-100 align-top">
                            <td className="py-2" colSpan={2}>
                              <div className="flex justify-between items-start gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium">{item.garmentLabel}</p>
                                  {item.fabricSelection && (
                                    <p className="text-xs text-gray-500">Fabric: {item.fabricSelection.rc_code}</p>
                                  )}
                                  {item.constructionType && (
                                    <p className="text-xs text-gray-400">{item.constructionType}</p>
                                  )}
                                </div>
                                <span className="py-0 text-right font-medium shrink-0">${item.price?.toFixed(2)}</span>
                              </div>
                              <InvoiceItemDetails
                                includeJacket={item.includeJacket}
                                jacketStartingSpecs={item.jacketStartingSpecs}
                                jacketAdjustments={item.jacketAdjustments}
                                jacketStyling={item.jacketStyling}
                                includeTrouser={item.includeTrouser}
                                trouserStartingSpecs={item.trouserStartingSpecs}
                                trouserAdjustments={item.trouserAdjustments}
                                pantStyling={item.pantStyling}
                                includeVest={item.includeVest}
                                vestStartingSpecs={item.vestStartingSpecs}
                                vestAdjustments={item.vestAdjustments}
                                vestStyling={item.vestStyling}
                                testIdPrefix={`invoice-saved-${idx}`}
                              />
                            </td>
                          </tr>
                        ))}
                        {garmentType && (
                          <tr className="border-b border-gray-100 align-top">
                            <td className="py-2" colSpan={2}>
                              <div className="flex justify-between items-start gap-3">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium">{GARMENT_TYPES.find(g => g.value === garmentType)?.label}</p>
                                  {fabricSelection && (
                                    <p className="text-xs text-gray-500">Fabric: {fabricSelection.rc_code}</p>
                                  )}
                                  <p className="text-xs text-gray-400">{constructionType}</p>
                                </div>
                                <span className="py-0 text-right font-medium shrink-0">${orderPrice?.toFixed(2)}</span>
                              </div>
                              <InvoiceItemDetails
                                includeJacket={includeJacket}
                                jacketStartingSpecs={jacketStartingSpecs}
                                jacketAdjustments={jacketAdjustments}
                                jacketStyling={jacketStyling}
                                includeTrouser={includeTrouser}
                                trouserStartingSpecs={trouserStartingSpecs}
                                trouserAdjustments={trouserAdjustments}
                                pantStyling={pantStyling}
                                includeVest={includeVest}
                                vestStartingSpecs={vestStartingSpecs}
                                vestAdjustments={vestAdjustments}
                                vestStyling={vestStyling}
                                testIdPrefix="invoice-current"
                              />
                            </td>
                          </tr>
                        )}
                        {rushOrder && (
                          <tr className="border-b border-gray-100">
                            <td className="py-2 text-amber-600">Rush Order Fee</td>
                            <td className="py-2 text-right text-amber-600">$100.00</td>
                          </tr>
                        )}
                      </tbody>
                    </table>

                    {/* Totals */}
                    <div className="space-y-1 text-sm border-t pt-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal</span>
                        <span>${(totals.subtotal + totals.rushFee).toFixed(2)}</span>
                      </div>
                      {totals.discountAmount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount ({discountType === "percent" ? `${discount}%` : "Fixed"})</span>
                          <span>-${totals.discountAmount.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax ({taxRate}%)</span>
                        <span>${totals.taxAmount.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t">
                        <span>Total</span>
                        <span>${totals.total.toFixed(2)}</span>
                      </div>
                    </div>

                    {/* Payment Info */}
                    <div className="mt-4 p-3 bg-gray-50 rounded-lg text-xs">
                      <p className="text-gray-500 font-medium mb-1">PAYMENT METHODS</p>
                      <div className="flex gap-4">
                        <span><span className="text-gray-500">Venmo:</span> @Richardlaguerre</span>
                        <span><span className="text-gray-500">Zelle:</span> 9146194784</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Payment Section */}
                <div className="bg-[#111c2e] rounded-xl border border-white/10 overflow-hidden">
                  {/* Amount Due */}
                  <div className="p-6 text-center border-b border-white/10">
                    <p className="text-sm text-slate-400 mb-2">AMOUNT DUE</p>
                    <p className={`text-5xl font-bold ${totals.balanceDue > 0 ? "text-white" : "text-green-400"}`}>
                      ${totals.balanceDue.toFixed(2)}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">Total: ${totals.total.toFixed(2)}</p>
                  </div>

                  {/* Quick Pay */}
                  <div className="p-4 border-b border-white/10">
                    <p className="text-sm font-medium text-slate-400 mb-3">QUICK PAY - FULL AMOUNT</p>
                    <div className="grid grid-cols-5 gap-2">
                      {PAYMENT_METHODS.map(method => (
                        <button
                          key={method.value}
                          onClick={() => payFull(method.value)}
                          disabled={totals.balanceDue <= 0}
                          className={`p-3 rounded-lg border border-white/10 hover:border-white/30 flex flex-col items-center gap-2 ${
                            totals.balanceDue <= 0 ? "opacity-50 cursor-not-allowed" : ""
                          }`}
                        >
                          <div className={`w-10 h-10 rounded-full ${method.color} flex items-center justify-center`}>
                            <method.icon size={20} className="text-white" />
                          </div>
                          <span className="text-xs">{method.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Payments Made */}
                  {payments.length > 0 && (
                    <div className="p-4 border-b border-white/10">
                      <p className="text-sm font-medium text-slate-400 mb-2">PAYMENTS RECEIVED</p>
                      {payments.map(p => (
                        <div key={p.id} className="flex justify-between items-center py-2 border-b border-white/5">
                          <div className="flex items-center gap-2">
                            <Check size={16} className="text-green-400" />
                            <span className="capitalize">{p.method}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-green-400">${p.amount.toFixed(2)}</span>
                            <button onClick={() => removePayment(p.id)} className="text-red-400 hover:text-red-300"><X size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Split Payment */}
                  {totals.balanceDue > 0 && (
                    <div className="p-4 border-b border-white/10">
                      <p className="text-sm font-medium text-slate-400 mb-2">SPLIT PAYMENT</p>
                      <div className="flex gap-2">
                        <Select value={currentPaymentMethod} onValueChange={setCurrentPaymentMethod}>
                          <SelectTrigger className="w-32 bg-[#0a1628] border-white/10"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {PAYMENT_METHODS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input type="number" placeholder="Amount" value={currentPaymentAmount} onChange={(e) => setCurrentPaymentAmount(e.target.value)} className="flex-1 bg-[#0a1628] border-white/10" />
                        <Button onClick={addPayment} className="bg-blue-500 hover:bg-blue-600"><Plus size={16} /></Button>
                      </div>
                    </div>
                  )}

                  {/* Complete */}
                  <div className="p-4">
                    <Button
                      onClick={completeSale}
                      disabled={isLoading || totals.balanceDue > 0}
                      className={`w-full h-14 text-lg ${totals.balanceDue <= 0 ? "bg-amber-500 hover:bg-amber-600" : "bg-slate-600"}`}
                    >
                      {isLoading ? <><Loader2 size={20} className="mr-2 animate-spin" />Processing...</> :
                       totals.balanceDue > 0 ? <><AlertCircle size={20} className="mr-2" />Complete Payment First</> :
                       <><Check size={20} className="mr-2" />Complete & Generate Invoice</>}
                    </Button>
                  </div>

                  {/* Send Email */}
                  {selectedClient?.email && (
                    <div className="p-4 border-t border-white/10">
                      <Button 
                        variant="outline"
                        className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/20"
                        disabled={totals.balanceDue > 0}
                      >
                        <Mail size={18} className="mr-2" />
                        Send to {selectedClient.email}
                      </Button>
                    </div>
                  )}

                  {/* PAYMENT Section */}
                  <div className="p-4 border-t border-white/10">
                    <p className="text-xs font-medium text-slate-400 mb-3">PAYMENT</p>
                    <div className="space-y-2">
                      {/* Pay with Card - Stripe */}
                      <button 
                        className={`w-full flex items-center gap-3 p-3 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg hover:from-indigo-700 hover:to-purple-700 text-white transition-all ${totals.balanceDue <= 0 || isProcessingStripe ? "opacity-50 cursor-not-allowed" : ""}`}
                        onClick={initiateStripePayment}
                        disabled={totals.balanceDue <= 0 || isProcessingStripe}
                        data-testid="pay-with-card-btn"
                      >
                        {isProcessingStripe ? (
                          <>
                            <Loader2 size={20} className="animate-spin" />
                            <span className="font-medium">Processing...</span>
                          </>
                        ) : (
                          <>
                            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                              <CreditCard size={16} className="text-white" />
                            </div>
                            <div className="flex-1 text-left">
                              <span className="font-medium">Pay with Card</span>
                              <span className="text-xs text-white/70 block">Visa, Mastercard, Amex</span>
                            </div>
                            <span className="font-bold">${totals.balanceDue.toFixed(2)}</span>
                          </>
                        )}
                      </button>
                      
                      <div className="relative my-3">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-white/10"></div>
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-[#111c2e] text-slate-500">or other methods</span>
                        </div>
                      </div>
                      
                      <button 
                        className={`w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white ${totals.balanceDue <= 0 ? "opacity-50" : ""}`}
                        onClick={() => {
                          if (totals.balanceDue > 0) {
                            payFull("cash");
                          }
                        }}
                        disabled={totals.balanceDue <= 0}
                      >
                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center">
                          <Check size={16} className="text-emerald-400" />
                        </div>
                        <span className="font-medium">Mark as paid</span>
                      </button>
                      {totals.balanceDue <= 0 && (
                        <button 
                          className="w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                          onClick={() => setPayments([])}
                        >
                          <div className="w-8 h-8 rounded-full bg-orange-500/20 flex items-center justify-center">
                            <X size={16} className="text-orange-400" />
                          </div>
                          <span className="font-medium">Mark as unpaid</span>
                        </button>
                      )}
                      <button 
                        className="w-full flex items-center gap-3 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                        onClick={() => {
                          const amount = prompt("Enter payment amount:");
                          if (amount && !isNaN(parseFloat(amount))) {
                            setPayments(prev => [...prev, {
                              id: `pay-${Date.now()}`,
                              method: "cash",
                              amount: parseFloat(amount),
                              date: new Date().toISOString()
                            }]);
                            toast.success(`Recorded $${parseFloat(amount).toFixed(2)} payment`);
                          }
                        }}
                      >
                        <div className="w-8 h-8 rounded-full bg-slate-500/20 flex items-center justify-center">
                          <Banknote size={16} className="text-slate-400" />
                        </div>
                        <span className="font-medium">Record payment</span>
                      </button>
                    </div>
                  </div>

                  {/* EXPORT Section */}
                  <div className="p-4 border-t border-white/10">
                    <p className="text-xs font-medium text-slate-400 mb-3">EXPORT</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button 
                        className="flex items-center justify-center gap-2 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                        onClick={() => window.print()}
                      >
                        <Printer size={18} className="text-blue-400" />
                        <span className="font-medium text-sm">Print</span>
                      </button>
                      <button 
                        className="flex items-center justify-center gap-2 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white"
                      >
                        <Download size={18} className="text-green-400" />
                        <span className="font-medium text-sm">PDF</span>
                      </button>
                    </div>
                  </div>

                  {/* 3-Dot Menu */}
                  <div className="p-4 border-t border-white/10">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="w-full flex items-center justify-center gap-2 p-3 bg-[#0a1628] rounded-lg hover:bg-[#162033] text-white">
                          <MoreVertical size={18} />
                          <span className="font-medium text-sm">More Options</span>
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="center" className="w-56 bg-[#1a2744] border-white/10 text-white">
                        <DropdownMenuItem className="hover:bg-white/10 cursor-pointer" onClick={() => window.print()}>
                          <Printer size={16} className="mr-2" />Print Invoice
                        </DropdownMenuItem>
                        <DropdownMenuItem className="hover:bg-white/10 cursor-pointer">
                          <Download size={16} className="mr-2" />Export as PDF
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        {selectedClient?.email && (
                          <DropdownMenuItem className="hover:bg-white/10 cursor-pointer">
                            <Mail size={16} className="mr-2" />Email Invoice
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem className="hover:bg-white/10 cursor-pointer">
                          <Copy size={16} className="mr-2" />Copy Invoice
                        </DropdownMenuItem>
                        <DropdownMenuSeparator className="bg-white/10" />
                        <DropdownMenuItem 
                          className="hover:bg-white/10 cursor-pointer"
                          onClick={() => totals.balanceDue > 0 ? payFull("cash") : setPayments([])}
                        >
                          {totals.balanceDue > 0 ? (
                            <><Check size={16} className="mr-2 text-emerald-400" />Mark as Paid</>
                          ) : (
                            <><X size={16} className="mr-2 text-orange-400" />Mark as Unpaid</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="hover:bg-white/10 cursor-pointer">
                          <FileText size={16} className="mr-2" />Mark as Sent
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Navigation */}
        {!showInvoice && (
          <div className="wizard-step-nav max-w-3xl mx-auto mt-6 flex justify-between">
            {stepOrder.indexOf(currentStep) > 0 ? (
              <Button variant="outline" onClick={prevStep} className="border-white/20">
                <ArrowLeft size={16} className="mr-2" />Back
              </Button>
            ) : <div />}
            
            {currentStep !== 5 && (
              <Button onClick={nextStep} disabled={!canProceed()} className="bg-amber-500 hover:bg-amber-600">
                {currentStep === 4 ? "Proceed to Payment" : "Next"}
                <ArrowRight size={16} className="ml-2" />
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      <AnimatePresence>
        {showInvoice && currentInvoice && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
            >
              <div className="bg-[#111c2e] p-4 flex items-center justify-between">
                <h3 className="font-bold text-lg text-white flex items-center gap-2">
                  <Check size={20} className="text-green-400" />Order Complete!
                </h3>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={handlePrint} className="text-white border-white/30">
                    <Printer size={16} className="mr-1" />Print
                  </Button>
                  <Button size="sm" variant="outline" onClick={handleExportPdf} disabled={isExportingPdf} className="text-white border-white/30">
                    {isExportingPdf ? <Loader2 size={16} className="mr-1 animate-spin" /> : <Download size={16} className="mr-1" />}PDF
                  </Button>
                  {currentInvoice?.first_mtm_order_id ? (
                    <OrderActions
                      orderId={currentInvoice.first_mtm_order_id}
                      orderNumber={currentInvoice.mtm_order_number}
                      defaultPhone={currentInvoice.client_phone || ""}
                      defaultEmail={currentInvoice.client_email || ""}
                      variant="inline"
                      testIdPrefix="pos-invoice-actions"
                    />
                  ) : null}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6 bg-white">
                <div ref={invoiceRef}>
                  <div className="text-center mb-6 pb-4 border-b border-gray-200">
                    <h1 className="text-2xl font-bold text-gray-900">LAGUERRE</h1>
                    <p className="text-sm text-gray-600">55 W Railroad Ave Building 24, Garnerville, NY 10923</p>
                    <p className="text-sm text-gray-600">hello@laguerre.co | www.laguerre.co</p>
                  </div>

                  <div className="flex justify-between mb-6">
                    <div>
                      <p className="text-xs text-gray-500">BILL TO</p>
                      <p className="font-medium text-gray-900">{currentInvoice.client_name}</p>
                      {currentInvoice.client_email && <p className="text-sm text-gray-600">{currentInvoice.client_email}</p>}
                      {currentInvoice.client_phone && <p className="text-sm text-gray-600">{currentInvoice.client_phone}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500">INVOICE</p>
                      <p className="font-bold text-lg text-gray-900">{currentInvoice.invoice_number}</p>
                      <p className="text-sm text-gray-600">{format(new Date(), "MMM d, yyyy")}</p>
                      <p className="text-sm text-gray-600">MTM: {currentInvoice.mtm_order_number}</p>
                    </div>
                  </div>

                  <table className="w-full mb-6">
                    <thead>
                      <tr className="border-b-2 border-gray-200">
                        <th className="text-left py-2 text-xs text-gray-500">Description</th>
                        <th className="text-right py-2 text-xs text-gray-500 w-24">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentInvoice.items?.map((item, idx) => (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-2 text-sm text-gray-900">{item.description}</td>
                          <td className="py-2 text-sm text-gray-900 text-right">${item.total?.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="flex justify-end mb-6">
                    <div className="w-64 space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Subtotal</span>
                        <span>${currentInvoice.subtotal?.toFixed(2)}</span>
                      </div>
                      {currentInvoice.discount > 0 && (
                        <div className="flex justify-between text-sm text-green-600">
                          <span>Discount</span>
                          <span>-${currentInvoice.discount?.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Tax</span>
                        <span>${currentInvoice.tax_amount?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-lg font-bold pt-2 border-t border-gray-200">
                        <span>Total</span>
                        <span>${currentInvoice.total?.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-green-600">
                        <span>Paid</span>
                        <span>-${currentInvoice.deposit_paid?.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-amber-50 rounded-lg p-4 mb-4">
                    <p className="text-xs text-gray-500 mb-1">ESTIMATED ARRIVAL</p>
                    <p className="font-medium text-gray-900">{format(new Date(currentInvoice.estimated_arrival || addWeeks(new Date(), 4)), "MMMM d, yyyy")}</p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-xs text-gray-500 mb-2">PAYMENT METHODS</p>
                    <div className="flex gap-6 text-sm">
                      <div><span className="text-gray-600">Venmo:</span> <span className="font-medium">@Richardlaguerre</span></div>
                      <div><span className="text-gray-600">Zelle:</span> <span className="font-medium">9146194784</span></div>
                    </div>
                  </div>

                  <div className="text-center mt-6 text-sm text-gray-500">
                    <p>Thank you for your business!</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#111c2e] p-4 flex justify-between">
                <Button variant="ghost" onClick={() => setShowInvoice(false)} className="text-slate-400">Close</Button>
                <Button onClick={resetOrder} className="bg-amber-500 hover:bg-amber-600">
                  <Plus size={16} className="mr-2" />New Order
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Sticky mobile wizard footer — only visible when body.mobile-mode is on */}
      <MobileWizardFooter
        onBack={prevStep}
        onNext={currentStep === 5 ? completeSale : nextStep}
        backDisabled={stepOrder.indexOf(currentStep) === 0}
        nextDisabled={
          currentStep === 5
            ? isLoading || totals.balanceDue > 0
            : !canProceed()
        }
        nextLoading={currentStep === 5 && isLoading}
        nextLabel={currentStep === 5 ? "Complete Order" : "Next"}
        nextVariant={currentStep === 5 ? "submit" : "primary"}
        testId="pos-mobile-footer"
      />
    </div>
  );
};

export default AdminPOSPage;
